import _ from 'lodash';
import {
  Button,
  Checkbox,
  Dropdown,
  Input,
  InputNumber,
  MenuProps,
  Select,
  TableProps,
  Tag,
  Tooltip,
  Typography,
  DatePicker
} from 'antd';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

import {
  CheckOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  EditFilled,
  InfoCircleOutlined,
  MinusOutlined,
  PlusOutlined,
  PrinterOutlined,
  UpOutlined,
  UserOutlined
} from '@ant-design/icons';
import { ReactComponent as IconClose } from 'assets/images/close.svg';
import { ReactComponent as IconBed } from 'assets/images/bed.svg';
import { ReactComponent as IconExclamation } from 'assets/images/exclamation-circle.svg';
import { ReactComponent as IconPlusCircle } from 'assets/images/plus-circle.svg';
import { ReactComponent as IconCalendar } from 'assets/images/calendar.svg';
import { ReactComponent as IconSwap } from 'assets/images/swap.svg';
// import { ReactComponent as IconAssign } from 'assets/images/assign.svg';
import { ReactComponent as IconCancelAssign } from 'assets/images/cancel-assign.svg';
// import { ReactComponent as IconStop } from "assets/images/stop.svg";
// import { ReactComponent as IconCopy } from "assets/images/copy.svg";
import { ReactComponent as IconBaby } from 'assets/images/baby.svg';
import { ReactComponent as IconClean } from 'assets/images/clean-v2.svg';
import { ReactComponent as IconDirty } from 'assets/images/dirty.svg';
import { ReactComponent as IconFix } from 'assets/images/fix.svg';
import { ReactComponent as IconMore } from 'assets/images/more.svg';

import {
  PersonalInfoType,
  RoomAssignType,
  RoomLockType,
  RoomType,
  ServiceType
} from 'services/api/type/room.type';
import {
  BookingLine,
  BookingStatus,
  CategoriesBookingReminderType,
  RoomStatus,
  Surveys
} from 'services/api/type/booking.type';
import { REMIND_CATEGORIES_OPTIONS } from 'components/modal/booking-remind/remind-categories-option';
import {
  formatCurrency,
  formatInputMoney,
  formatMoneyValue,
  formatNumber,
  renderTextWithEllipsis,
  renderWithLineThrough,
  renderWithRowSpan,
  toFormatDate,
  toLocalTime
} from 'utils';
import {
  BOOKING_STATUS,
  getExpenseTypeOptions,
  LIST_GROUP_STATUS_NAMES,
  getVipLevelMap,
  PAYMENT_STATUS,
  PAYMENT_TYPE,
  TOUR_STATUS_NAMES,
  PAYMENT_STATUS_ACC_NAMES
} from './form';
import {
  CASHFLOW_TYPE,
  GENDER_NAME,
  GUEST_LEVEL_NAME,
  MAP_GENDER,
  MAP_GUEST_LEVEL
} from './common';
import { CountryType } from 'services/api/type/common.type';
import { PAGE_SIZE_CASH_FLOW, PAGE_SIZE_STAYING_GUESTS } from 'services/api/module/report.api';
import { AllPaymentItem } from 'services/api/type/payment.type';
// import { RoomNoRegex } from './regex';
import { getMapPaymentType } from './text';
import {
  CashFlowType,
  DetailedAnalysisReport,
  FolioRevenueReport,
  NationalityStatisticType
} from 'services/api/type/report.type';
import { ShiftHandover } from 'services/api/type/shift-handover.type';
import { WarehouseProductsType } from 'services/api/type/warehouse-management.type';
import { ResponseGroupBookings, SubGroup } from 'services/api/type/group.type';
import { formatInputNumber } from 'utils/currency';
import { TravelAgencyResponse } from 'services/api/type/travel-agency.type';
import { ServiceKinSimUrlsType } from 'services/api/type/service.type';
import { QRCodeSVG } from 'qrcode.react';
import { renderRoomsDropdown } from 'components/booking-list/renderRoomsDropdown';
import { CustomMenuItem, PermissionActionTypes } from 'helper/type/action.type';
import { useFilteredActionBookingList } from 'components/booking-list/action-booking-list';
import { ACTION_BOOKING } from 'components/booking-list/action-booking';
import { ACTION_TOUR_LIST } from 'components/tour/action-tour-list';
import IBButton from 'helper/permission.action';
import { BookingData, TourData } from '@/pages/tour';

export const getMappingColumnBookingStatus = (
  t: (key: string) => string
): Record<string, string> => ({
  [BOOKING_STATUS.CONFIRM]: t('bookingPage.bookingList.bookingStatus.confirm'),
  [BOOKING_STATUS.ALLOT]: t('bookingPage.bookingList.bookingStatus.allot'),
  [BOOKING_STATUS.CANCEL]: t('bookingPage.bookingList.bookingStatus.cancel'),
  [BOOKING_STATUS.CHECKOUT]: t('bookingPage.bookingList.bookingStatus.checkOut')
});

export const getNightAuditStatusMap = (t: (key: string) => string): Record<string, string> => ({
  [BookingStatus.CONFIRM]: t('bookingPage.bookingList.bookingStatus.confirm'),
  [BookingStatus.CHECK_IN]: t('bookingPage.bookingList.bookingStatus.checkIn'),
  [BookingStatus.CHECK_OUT]: t('bookingPage.bookingList.bookingStatus.checkOut')
});

function getViewKinSimDetailsStatusBadgeColor(status: string): string {
  switch (status?.toLowerCase()) {
    case 'available':
      return 'default';
    case 'claimed':
      return 'blue';
    case 'redeemed':
      return 'green';
    default:
      return 'default';
  }
}

export const getColumnAssignRoom = (
  t: (key: string) => string
): TableProps<RoomAssignType>['columns'] => [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    width: '106px'
  },
  {
    title: t('bookingPage.booking.roomName'),
    dataIndex: 'roomName',
    key: 'roomName'
  },
  {
    title: t('bookingPage.booking.night'),
    dataIndex: 'night',
    key: 'night',
    width: '170px'
  },
  {
    title: t('common.customer.adult'),
    dataIndex: 'adult',
    key: 'adult',
    width: '170px'
  },
  {
    title: t('common.customer.child'),
    dataIndex: 'numberChildren',
    key: 'numberChildren',
    width: '170px'
  },
  {
    title: t('common.bookingData.price'),
    key: 'price',
    dataIndex: 'price',
    width: '170px',
    render: (_, { price }) => `${formatCurrency(price)} đ`
  }
];

export const fakeDataAssignRoom: RoomAssignType[] = [
  {
    key: 1,
    id: '000123',
    roomName: '9DS81 Kin Inspired 1- 301',
    night: 1,
    adult: 2,
    numberChildren: 1,
    price: 18000000
  },
  {
    key: 2,
    id: '000124',
    roomName: '7HK99 Kin Inspired 1- 777',
    night: 1,
    adult: 3,
    numberChildren: 2,
    price: 20000000
  }
];

export const getColumnService = (
  t: (key: string) => string
): TableProps<ServiceType>['columns'] => [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    width: '106px'
  },
  {
    title: t('common.bookingData.services.serviceName'),
    dataIndex: 'serviceName',
    key: 'serviceName'
  },
  {
    title: t('common.bookingData.services.qty'),
    dataIndex: 'quantity',
    key: 'quantity',
    width: '220px'
  },
  {
    title: t('common.bookingData.price'),
    key: 'price',
    dataIndex: 'price',
    width: '220px',
    render: (_, { price }) => `${formatCurrency(price)} đ`
  }
];

export const getActionBookingGroupList = (t: (key: string) => string): MenuProps['items'] => [
  {
    key: ACTION_BOOKING.DETAIL_GROUP,
    label: (
      <div className="action-item">
        <IconExclamation />
        <span>{t('groupPage.groupDetail.title')}</span>
      </div>
    )
  },
  {
    label: (
      <div className="action-item">
        <PrinterOutlined style={{ color: '#00000040' }} />
        <span>{t('groupPage.groupDetail.printConfirmation')}</span>
      </div>
    ),
    key: ACTION_BOOKING.PRINT_GROUP
  },
  {
    label: (
      <div className="action-item">
        <IconClose />
        <span>{t('groupPage.groupDetail.cancelGroup')}</span>
      </div>
    ),
    key: ACTION_BOOKING.CANCEL_GROUP
  }
];

export const getBookingListColumns = (
  onClick: MenuProps['onClick'],
  isUseActualTime: boolean = false,
  isUseActualCheckIn: boolean = false,
  t: (key: string) => string,
  showRoomStatus: boolean = false,
  showCheckInExpSurvey: boolean = false,
  showCheckOutExpSurvey: boolean = false,
  onClickEdit?: (bookingLineId: number, selectedRoomId?: number) => void,
  availableRooms?: any[],
  setRangeDate?: any,
  setFilterOptions?: (filterOptions: any) => void
) => {
  const MappingColumnBookingStatus = getMappingColumnBookingStatus(t);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const filteredActions = useFilteredActionBookingList(
    t,
    showCheckInExpSurvey || showCheckOutExpSurvey
  );
  const columnBookingList: TableProps<BookingLine>['columns'] = [
    {
      title: t('common.bookingData.bookingId'),
      dataIndex: 'booking_id',
      key: 'booking_id',
      width: '120px',
      align: 'center'
    },
    {
      title: t('common.bookingData.status'),
      dataIndex: 'status',
      key: 'status',
      width: '100px',
      render: text =>
        MappingColumnBookingStatus[text as keyof typeof MappingColumnBookingStatus] || ''
    },
    {
      title: t('common.bookingData.room'),
      dataIndex: 'room_name',
      key: 'room_name',
      width: '160px',
      render: (_, record) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="room-id">
            {record.room_name || (
              <div style={{ color: 'red' }}>{record.room_type_name + ' N/A'}</div>
            )}
          </span>
          {onClickEdit && renderRoomsDropdown(record, availableRooms, onClickEdit, setRangeDate)}
        </div>
      )
    },
    {
      title: t('common.customer.name'),
      dataIndex: 'partner_name',
      key: 'partner_name',
      width: '160px'
    },
    {
      title: t('common.customer.gender'),
      dataIndex: 'gender',
      key: 'gender',
      width: '90px',
      render: value => {
        const genderCode = MAP_GENDER[value]; // e.g. 'F'
        const genderKey = GENDER_NAME[genderCode as keyof typeof GENDER_NAME]; // e.g. 'common.customer.female'
        return t(genderKey); // e.g. t('common.customer.female') => "Female"
      }
    },
    {
      title: t('bookingPage.bookingList.ta'),
      dataIndex: 'hotel_travel_agency_name',
      key: 'hotel_travel_agency_name',
      width: '160px'
    },
    {
      title: t('bookingPage.bookingList.adultAndChild'),
      dataIndex: 'adult',
      key: 'adult',
      width: '100px'
    },
    {
      title: t('common.note'),
      dataIndex: 'note',
      key: 'note',
      width: '200px',
      render: renderTextWithEllipsis
    },
    {
      title: t('bookingPage.bookingList.labels'),
      dataIndex: 'labels',
      key: 'labels',
      width: '160px',
      render: (value, record) => (
        <span className="labels">
          {record.labels.map(label => (
            <span
              key={label.name}
              style={{
                backgroundColor: label.color,
                color: '#fff',
                padding: '2px 5px',
                margin: '0 2px',
                borderRadius: '3px'
              }}
            >
              {label.name}
            </span>
          ))}
        </span>
      )
    },
    {
      title: 'FOC',
      dataIndex: 'is_foc',
      key: 'is_foc',
      width: '60px',
      render: value => {
        if (value) {
          return (
            <Tag
              color="success"
              style={{
                backgroundColor: '#E4FBE5',
                fontWeight: 'bold'
              }}
            >
              FOC
            </Tag>
          );
        }
      }
    },
    {
      title: t('common.time.checkIn'),
      dataIndex: 'check_in',
      key: 'check_in',
      width: '180px',
      render: value => dayjs(value).format('DD/MM/YYYY - HH:mm')
    },
    {
      title: t('common.time.checkOut'),
      dataIndex: 'check_out',
      key: 'check_out',
      render: value => dayjs(value).format('DD/MM/YYYY - HH:mm'),
      width: '180px'
    },
    {
      title: t('common.bookingData.priceList'),
      dataIndex: 'pricelist',
      key: 't',
      width: '120px',
      render: value => value?.name || t('common.bookingData.defaultPrice')
    },
    {
      title: t('bookingPage.bookingList.medium'),
      dataIndex: 'medium_name',
      key: 'medium_name',
      width: '130px'
    },
    {
      title: t('bookingPage.bookingList.campaign'),
      dataIndex: 'campaign_name',
      key: 'campaign_name',
      width: '130px'
    },
    {
      title: t('bookingPage.bookingList.otaId'),
      dataIndex: 'cms_ota_id',
      key: 'cms_ota_id',
      width: '130px',
      render: renderTextWithEllipsis
    },
    {
      key: 'data_group',
      render: (_, record) => {
        if (!record.expandableRows) {
          return null;
        }
        return record.group_master && record.group_master.id && !record.is_foc ? (
          <>
            Group:{' '}
            <>
              <a
                href="#"
                className="hightlight"
                onClick={e => {
                  e.preventDefault();
                  setFilterOptions ? setFilterOptions({ group_id: record.group_master?.id }) : '';
                }}
              >
                {record.group_master.id}
              </a>
            </>{' '}
            - {record.group_master.name} | Party:{' '}
            <>
              <a
                href="#"
                className="hightlight"
                onClick={e => {
                  e.preventDefault();
                  setFilterOptions ? setFilterOptions({ booking_id: record.booking_id }) : '';
                }}
              >
                {record.booking_id}
              </a>{' '}
              ({record.expandableRows?.length})
            </>
          </>
        ) : !!record.foc_level && !!record.reason_approve_by && !!record.is_foc ? (
          `${(() => {
            const genderKey = MAP_GUEST_LEVEL[record.foc_level];
            return GUEST_LEVEL_NAME[genderKey as keyof typeof GUEST_LEVEL_NAME] || '';
          })()} + ${record.reason_approve_by}`
        ) : (
          `${record?.partner_name} (${record.expandableRows?.length})`
        );
      }
    }
  ];

  if (isUseActualTime) {
    columnBookingList.splice(6, 0, {
      title: t('bookingPage.bookingList.actualCheckOut'),
      dataIndex: 'actual_check_out',
      key: 'actual_check_out',
      render: value => toLocalTime(value, 'DD/MM/YYYY - HH:mm'),
      width: '180px'
    });
  }

  if (isUseActualCheckIn) {
    columnBookingList.splice(6, 0, {
      title: t('bookingPage.bookingList.actualCheckIn'),
      dataIndex: 'actual_check_in',
      key: 'actual_check_in',
      render: value => toLocalTime(value, 'DD/MM/YYYY - HH:mm'),
      width: '180px'
    });
  }

  if (showRoomStatus) {
    columnBookingList.splice(3, 0, {
      title: t('bookingPage.bookingList.roomStatus'),
      dataIndex: 'room_status',
      align: 'center',
      key: 'room_status',
      render: (value: RoomStatus) => {
        if (value) {
          if (value.is_occupied) {
            return (
              <Tag
                color="error"
                style={{
                  backgroundColor: '#FFE7E0',
                  fontWeight: 'bold'
                }}
              >
                {t('common.statuses.roomOcc')}
              </Tag>
            );
          } else if (value.is_clean && !value.is_occupied) {
            return (
              <Tag
                color="success"
                style={{
                  backgroundColor: '#E4FBE5',
                  fontWeight: 'bold'
                }}
              >
                {t('common.statuses.roomReady')}
              </Tag>
            );
          } else if (!value.is_clean && !value.is_occupied) {
            return (
              <Tag
                style={{
                  backgroundColor: '#E2E0E0',
                  fontWeight: 'bold'
                }}
              >
                {t('common.statuses.dirty')}
              </Tag>
            );
          }
        }

        return null;
      },
      width: '180px'
    });
  }

  if (showCheckInExpSurvey || showCheckOutExpSurvey) {
    columnBookingList.splice(2, 0, {
      title: t('common.bookingData.surveyStatus.title'),
      dataIndex: 'surveys',
      align: 'center',
      render: (value: Surveys) => {
        if (value) {
          if (value.is_checkin && showCheckInExpSurvey && !showCheckOutExpSurvey) {
            return (
              <Tag
                color="success"
                style={{
                  fontWeight: 'bold'
                }}
              >
                {t('common.bookingData.surveyStatus.done')}
              </Tag>
            );
          } else if (value.is_checkout && !showCheckInExpSurvey && showCheckOutExpSurvey) {
            return (
              <Tag
                color="success"
                style={{
                  fontWeight: 'bold'
                }}
              >
                {t('common.bookingData.surveyStatus.done')}
              </Tag>
            );
          }
        }

        return (
          <Tag
            color="warning"
            style={{
              fontWeight: 'bold'
            }}
          >
            {t('common.bookingData.surveyStatus.notYet')}
          </Tag>
        );
      },
      width: '180px'
    });
  }

  columnBookingList.forEach(col => {
    if ((col as any).key === 'data_group') {
      col.onCell = record => ({
        colSpan: !_.isEmpty(record.expandableRows) ? 10 : 1
        // className: !RoomNoRegex.test(record.room_name) ? 'not-assigned' : ''
      });
    } else {
      col.onCell = record => ({
        colSpan: !_.isEmpty(record.expandableRows) ? 0 : 1
        // className: !RoomNoRegex.test(record.room_name) ? 'not-assigned' : ''
      });
    }

    const existingRender = col.render;

    col.render = (text, record, index) => {
      // if (!_.isEmpty(record.expandableRows)) {
      //   return (
      //     <div className="ant-table-cell__inner ant-table-cell__expand">
      //       <span>{text}</span>
      //       <Dropdown
      //         trigger={["click"]}
      //         menu={{ items: ACTION_BOOKING__GROUP_LIST, onClick }}
      //         placement="bottom"
      //         overlayClassName="action-booking-list"
      //         destroyPopupOnHide
      //       >
      //         <Button
      //           icon={<EllipsisOutlined style={{ fontSize: "18px" }} />}
      //           type="text"
      //         />
      //       </Dropdown>
      //     </div>
      //   );
      // }
      const menu = filteredActions.filter(item => {
        if (
          (item?.key === ACTION_BOOKING.CANCEL_BOOKING ||
            item?.key === ACTION_BOOKING.CANCEL_BOOKING_DIVIDER) &&
          record.status !== BookingStatus.CONFIRM
        ) {
          return false;
        }

        if (
          (item?.key === ACTION_BOOKING.GET_ROOM ||
            item?.key === ACTION_BOOKING.CANCEL_ASSIGN_ROOM) &&
          (record.status !== BookingStatus.CONFIRM ||
            (record.status === BookingStatus.CONFIRM && !record.room_id))
        ) {
          return false;
        }

        if (
          item?.key === ACTION_BOOKING.ASSIGN_ROOM &&
          (record.status !== BookingStatus.CONFIRM ||
            (record.status === BookingStatus.CONFIRM && record.room_id))
        ) {
          return false;
        }

        if (
          [
            ACTION_BOOKING.DETAIL_DIVIDER,
            ACTION_BOOKING.ADD_SERVICE,
            ACTION_BOOKING.CHANGE_DATE,
            ACTION_BOOKING.CHANGE_ROOM
          ].includes(String(item?.key)) &&
          [BookingStatus.CANCEL, BookingStatus.CHECK_OUT].includes(record.status)
        ) {
          return false;
        }
        if (item?.key === ACTION_BOOKING.DUPLICATE_BOOKING_LINE) {
          return true;
        }
        if (
          [ACTION_BOOKING.MOVE_BOOKING, ACTION_BOOKING.UNDO_MOVED_BOOKING].includes(
            String(item?.key)
          ) &&
          [BookingStatus.CANCEL, BookingStatus.CHECK_OUT].includes(record.status)
        ) {
          return false;
        }
        if (
          [ACTION_BOOKING.UNDO_MOVED_BOOKING].includes(String(item?.key)) &&
          record.moved_booking_id === record.booking_id
        ) {
          return false;
        }
        return true;
      });

      return (
        <Dropdown
          trigger={['click']}
          menu={{ items: menu, onClick }}
          placement="bottom"
          overlayClassName={classNames('action-booking-list', {
            'action-booking-list--auto': !_.isEmpty(record.expandableRows)
          })}
          destroyPopupOnHide
        >
          {(col as any).dataIndex === 'adult' ? (
            <div>
              {text}/{record.child}
            </div>
          ) : (
            <div>{existingRender ? existingRender(text, record, index) : text}</div>
          )}
        </Dropdown>
      );
    };
  });

  return columnBookingList;
};

export const getBookingListColumnsExpand = (
  onClick: MenuProps['onClick'],
  isUseActualTime: boolean = false,
  isUseActualCheckIn: boolean = false,
  t: (key: string) => string,
  showRoomStatus: boolean = false,
  showCheckInExpSurvey: boolean = false,
  showCheckOutExpSurvey: boolean = false,
  onClickEdit?: (bookingLineId: number, selectedRoomId?: number) => void,
  availableRooms?: any[],
  setRangeDate?: any
) => {
  const MappingColumnBookingStatus = getMappingColumnBookingStatus(t);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const filteredActions = useFilteredActionBookingList(
    t,
    showCheckInExpSurvey || showCheckOutExpSurvey
  );
  const columnBookingList: TableProps<BookingLine>['columns'] = [
    {
      title: t('common.bookingData.bookingId'),
      dataIndex: 'booking_id',
      key: 'booking_id',
      width: '120px',
      align: 'center'
    },
    {
      title: t('common.bookingData.status'),
      dataIndex: 'status',
      key: 'status',
      width: '100px',
      render: text => MappingColumnBookingStatus[text] || ''
    },
    {
      title: t('common.bookingData.room'),
      dataIndex: 'room_name',
      key: 'room_name',
      width: '180px',
      render: (_, record) => {
        const getRoomDisplay = () => {
          if (!record.room_name) {
            return (
              <div style={{ color: 'red' }}>
                {record.room_type_name + ' N/A'}
                {record.is_master_room ? (
                  <span style={{ fontWeight: 'bold', color: 'blue' }}> MAS.</span>
                ) : (
                  ''
                )}
              </div>
            );
          }
          return (
            <span className="room-id">
              {record.room_name}
              {record.is_master_room ? (
                <span style={{ fontWeight: 'bold', color: 'blue' }}> MAS.</span>
              ) : (
                ''
              )}
            </span>
          );
        };

        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {getRoomDisplay()}
            {onClickEdit && renderRoomsDropdown(record, availableRooms, onClickEdit, setRangeDate)}
          </div>
        );
      }
    },
    {
      title: t('common.customer.name'),
      dataIndex: 'partner_name',
      key: 'partner_name',
      width: '160px'
    },
    {
      title: t('common.customer.gender'),
      dataIndex: 'gender',
      key: 'gender',
      width: '90px',
      render: value => {
        const genderCode = MAP_GENDER[value]; // e.g. 'F'
        const genderKey = GENDER_NAME[genderCode as keyof typeof GENDER_NAME]; // e.g. 'common.customer.female'
        return t(genderKey); // e.g. t('common.customer.female') => "Female"
      }
    },
    {
      title: t('bookingPage.bookingList.ta'),
      dataIndex: 'hotel_travel_agency_name',
      key: 'hotel_travel_agency_name',
      width: '160px'
    },
    {
      title: t('bookingPage.bookingList.adultAndChild'),
      dataIndex: 'adult',
      key: 'adult',
      width: '100px'
    },
    {
      title: t('common.note'),
      dataIndex: 'note',
      key: 'note',
      width: '220px'
    },
    {
      title: t('bookingPage.bookingList.labels'),
      dataIndex: 'labels',
      key: 'labels',
      width: '160px',
      render: (value, record) => (
        <span className="labels">
          {record.labels.map(label => (
            <span
              key={label.name}
              style={{
                backgroundColor: label.color,
                color: '#fff',
                padding: '2px 5px',
                margin: '0 2px',
                borderRadius: '3px'
              }}
            >
              {label.name}
            </span>
          ))}
        </span>
      )
    },
    {
      title: 'FOC',
      dataIndex: 'is_foc',
      key: 'is_foc',
      width: '60px',
      render: value => {
        if (value) {
          return (
            <Tag
              color="success"
              style={{
                backgroundColor: '#E4FBE5',
                fontWeight: 'bold'
              }}
            >
              FOC
            </Tag>
          );
        }
      }
    },
    {
      title: t('common.time.checkIn'),
      dataIndex: 'check_in',
      key: 'check_in',
      render: value => dayjs(value).format('DD/MM/YYYY - HH:mm'),
      width: '180px'
    },
    {
      title: t('common.time.checkOut'),
      dataIndex: 'check_out',
      key: 'check_out',
      render: value => dayjs(value).format('DD/MM/YYYY - HH:mm'),
      width: '180px'
    },
    {
      title: t('common.bookingData.priceList'),
      dataIndex: 'pricelist',
      key: 't',
      width: '120px',
      render: value => value?.name || t('common.bookingData.defaultPrice')
    },
    {
      title: t('bookingPage.bookingList.medium'),
      dataIndex: 'medium_name',
      key: 'medium_name',
      width: '130px'
    },
    {
      title: t('bookingPage.bookingList.campaign'),
      dataIndex: 'campaign_name',
      key: 'campaign_name',
      width: '130px'
    },
    {
      title: t('bookingPage.bookingList.otaId'),
      dataIndex: 'cms_ota_id',
      key: 'cms_ota_id',
      width: '130px',
      render: renderTextWithEllipsis
    }
  ];

  if (isUseActualTime) {
    columnBookingList.splice(6, 0, {
      title: t('bookingPage.bookingList.actualCheckOut'),
      dataIndex: 'actual_check_out',
      key: 'actual_check_out',
      render: value => toLocalTime(value, 'DD/MM/YYYY - HH:mm'),
      width: '180px'
    });
  }

  if (isUseActualCheckIn) {
    columnBookingList.splice(6, 0, {
      title: t('bookingPage.bookingList.actualCheckIn'),
      dataIndex: 'actual_check_in',
      key: 'actual_check_in',
      render: value => toLocalTime(value, 'DD/MM/YYYY - HH:mm'),
      width: '180px'
    });
  }

  if (showRoomStatus) {
    columnBookingList.splice(3, 0, {
      title: t('bookingPage.bookingList.roomStatus'),
      dataIndex: 'room_status',
      align: 'center',
      key: 'room_status',
      render: (value: RoomStatus) => {
        if (value) {
          if (value.is_occupied) {
            return (
              <Tag
                color="error"
                style={{
                  backgroundColor: '#FFE7E0',
                  fontWeight: 'bold'
                }}
              >
                {t('common.statuses.roomOcc')}
              </Tag>
            );
          } else if (value.is_clean && !value.is_occupied) {
            return (
              <Tag
                color="success"
                style={{
                  backgroundColor: '#E4FBE5',
                  fontWeight: 'bold'
                }}
              >
                {t('common.statuses.roomReady')}
              </Tag>
            );
          } else if (!value.is_clean && !value.is_occupied) {
            return (
              <Tag
                style={{
                  backgroundColor: '#E2E0E0',
                  fontWeight: 'bold'
                }}
              >
                {t('common.statuses.dirty')}
              </Tag>
            );
          }
        }

        return null;
      },
      width: '180px'
    });
  }

  if (showCheckInExpSurvey || showCheckOutExpSurvey) {
    columnBookingList.splice(2, 0, {
      title: t('common.bookingData.surveyStatus.title'),
      dataIndex: 'surveys',
      align: 'center',
      render: (value: Surveys) => {
        if (value) {
          if (value.is_checkin && showCheckInExpSurvey && !showCheckOutExpSurvey) {
            return (
              <Tag
                color="success"
                style={{
                  fontWeight: 'bold'
                }}
              >
                {t('common.bookingData.surveyStatus.done')}
              </Tag>
            );
          } else if (value.is_checkout && !showCheckInExpSurvey && showCheckOutExpSurvey) {
            return (
              <Tag
                color="success"
                style={{
                  fontWeight: 'bold'
                }}
              >
                {t('common.bookingData.surveyStatus.done')}
              </Tag>
            );
          }
        }

        return (
          <Tag
            color="warning"
            style={{
              fontWeight: 'bold'
            }}
          >
            {t('common.bookingData.surveyStatus.notYet')}
          </Tag>
        );
      },
      width: '180px'
    });
  }

  columnBookingList.forEach(col => {
    const existingRender = col.render;

    // col.onCell = record => ({
    //   className: !RoomNoRegex.test(record.room_name) ? 'not-assigned' : ''
    // });
    col.render = (text, record, index) => {
      // eslint-disable-next-line
      const menu = filteredActions.filter(item => {
        if (
          (item?.key === ACTION_BOOKING.CANCEL_BOOKING ||
            item?.key === ACTION_BOOKING.CANCEL_BOOKING_DIVIDER) &&
          record.status !== BookingStatus.CONFIRM
        ) {
          return false;
        }

        if (
          (item?.key === ACTION_BOOKING.GET_ROOM ||
            item?.key === ACTION_BOOKING.CANCEL_ASSIGN_ROOM) &&
          (record.status !== BookingStatus.CONFIRM ||
            (record.status === BookingStatus.CONFIRM && !record.room_id))
        ) {
          return false;
        }

        if (
          item?.key === ACTION_BOOKING.ASSIGN_ROOM &&
          (record.status !== BookingStatus.CONFIRM ||
            (record.status === BookingStatus.CONFIRM && record.room_id))
        ) {
          return false;
        }

        if (
          [
            ACTION_BOOKING.DETAIL_DIVIDER,
            ACTION_BOOKING.ADD_SERVICE,
            ACTION_BOOKING.CHANGE_DATE,
            ACTION_BOOKING.CHANGE_ROOM
          ].includes(String(item?.key)) &&
          [BookingStatus.CANCEL, BookingStatus.CHECK_OUT].includes(record.status)
        ) {
          return false;
        }
        if (
          [ACTION_BOOKING.MOVE_BOOKING, ACTION_BOOKING.UNDO_MOVED_BOOKING].includes(
            String(item?.key)
          ) &&
          [BookingStatus.CANCEL, BookingStatus.CHECK_OUT].includes(record.status)
        ) {
          return false;
        }
        if (
          [ACTION_BOOKING.UNDO_MOVED_BOOKING].includes(String(item?.key)) &&
          record.moved_booking_id === record.booking_id
        ) {
          return false;
        }
        return true;
      });

      return (
        <Dropdown
          trigger={['click']}
          menu={{ items: menu, onClick }}
          placement="bottom"
          overlayClassName="action-booking-list"
          destroyPopupOnHide
        >
          {(col as any).dataIndex === 'adult' ? (
            <div>
              {text}/{record.child}
            </div>
          ) : (
            <div>{existingRender ? existingRender(text, record, index) : text}</div>
          )}
        </Dropdown>
      );
    };
  });

  return columnBookingList;
};

export const getActionRoomItemEmpty = (t: (key: string) => string): CustomMenuItem[] => [
  // {
  //   key: ACTION_BOOKING.QUICK_GET_ROOM,
  //   label: (
  //     <div className="action-item">
  //       <IconBed />
  //       <span>Nhận phòng nhanh</span>
  //     </div>
  //   ),
  // },
  {
    label: (
      <div className="action-item">
        <IconDirty />
        <span>{t('managePage.houseStatusPage.houseKeeping.dirtyRooms')}</span>
      </div>
    ),
    key: ACTION_BOOKING.DIRTY_ROOM,
    permission: PermissionActionTypes.SET_ROOM_STATUS
  },
  {
    label: (
      <div className="action-item">
        <IconClean />
        <span>{t('managePage.houseStatusPage.houseKeeping.clean')}</span>
      </div>
    ),
    key: ACTION_BOOKING.CLEAN_ROOM,
    permission: PermissionActionTypes.SET_ROOM_STATUS
  },
  {
    label: (
      <div className="action-item">
        <IconFix />
        <span>{t('managePage.houseStatusPage.houseKeeping.fixRoom')}</span>
      </div>
    ),
    key: ACTION_BOOKING.FIX_ROOM,
    permission: PermissionActionTypes.SET_ROOM_STATUS
  }
];

export const getActionRoomItem = (t: (key: string) => string): CustomMenuItem[] => [
  {
    key: ACTION_BOOKING.DETAIL,
    label: (
      <div className="action-item">
        <IconExclamation />
        <span>{t('common.actions.details')}</span>
      </div>
    ),
    permission: PermissionActionTypes.BOOKING_LIST_DETAIL
  },
  {
    label: (
      <div className="action-item">
        <IconDirty />
        <span>{t('managePage.houseStatusPage.houseKeeping.dirtyRooms')}</span>
      </div>
    ),
    key: ACTION_BOOKING.DIRTY_ROOM
  },
  {
    label: (
      <div className="action-item">
        <IconClean />
        <span>{t('managePage.houseStatusPage.houseKeeping.clean')}</span>
      </div>
    ),
    key: ACTION_BOOKING.CLEAN_ROOM,
    permission: PermissionActionTypes.SET_ROOM_STATUS
  },
  {
    label: (
      <div className="action-item">
        <IconBed />
        <span>{t('common.statuses.checkInRoom')}</span>
      </div>
    ),
    key: ACTION_BOOKING.GET_ROOM,
    permission: PermissionActionTypes.CHECK_IN
  },
  {
    label: (
      <div className="action-item">
        <IconCancelAssign />
        <span>{t('common.actions.unAssignRoom')}</span>
      </div>
    ),
    key: ACTION_BOOKING.CANCEL_ASSIGN_ROOM,
    permission: PermissionActionTypes.UNASSIGN_ROOM
  },
  {
    type: 'divider',
    dashed: true
  },
  {
    label: (
      <div className="action-item">
        <IconPlusCircle />
        <span>{t('common.actions.addServices')}</span>
      </div>
    ),
    key: ACTION_BOOKING.ADD_SERVICE,
    permission: PermissionActionTypes.ADD_SERVICE
  },
  {
    label: (
      <div className="action-item">
        <IconCalendar />
        <span>{t('common.actions.changeDate')}</span>
      </div>
    ),
    key: ACTION_BOOKING.CHANGE_DATE,
    permission: PermissionActionTypes.CHANGE_DATE
  },
  {
    label: (
      <div className="action-item">
        <IconSwap />
        <span>{t('common.actions.changeRoom')}</span>
      </div>
    ),
    key: ACTION_BOOKING.CHANGE_ROOM,
    permission: PermissionActionTypes.CHANGE_ROOM
  },
  {
    label: (
      <div className="action-item">
        <IconFix />
        <span>{t('managePage.houseStatusPage.houseKeeping.fixRoom')}</span>
      </div>
    ),
    key: ACTION_BOOKING.FIX_ROOM,
    permission: PermissionActionTypes.SET_ROOM_STATUS
  },
  {
    type: 'divider',
    key: `${ACTION_BOOKING.CANCEL_BOOKING}-divider`
  },
  {
    label: (
      <div className="action-item">
        <IconClose />
        <span>{t('common.actions.cancelBooking')}</span>
      </div>
    ),
    key: ACTION_BOOKING.CANCEL_BOOKING
  }
  // {
  //   label: (
  //     <div className="action-item">
  //       <IconCopy />
  //       <span>Sao chép</span>
  //     </div>
  //   ),
  //   key: ACTION_BOOKING.COPY,
  // },
];

export const getActionCleaningCalendar = (t: (key: string) => string): CustomMenuItem[] => [
  {
    key: ACTION_BOOKING.DETAIL,
    label: (
      <div className="action-item">
        <IconExclamation />
        <span>{t('common.actions.details')}</span>
      </div>
    ),
    permission: PermissionActionTypes.BOOKING_LIST_DETAIL
  },
  {
    label: (
      <div className="action-item">
        <IconDirty />
        <span>{t('managePage.houseStatusPage.houseKeeping.dirtyRooms')}</span>
      </div>
    ),
    key: ACTION_BOOKING.DIRTY_ROOM,
    permission: PermissionActionTypes.SET_ROOM_STATUS
  },
  {
    label: (
      <div className="action-item">
        <IconClean />
        <span>{t('managePage.houseStatusPage.houseKeeping.clean')}</span>
      </div>
    ),
    key: ACTION_BOOKING.CLEAN_ROOM,
    permission: PermissionActionTypes.SET_ROOM_STATUS
  },
  {
    label: (
      <div className="action-item">
        <IconPlusCircle />
        <span>{t('common.actions.addServices')}</span>
      </div>
    ),
    key: ACTION_BOOKING.ADD_SERVICE,
    permission: PermissionActionTypes.ADD_SERVICE
  },
  {
    label: (
      <div className="action-item">
        <IconFix />
        <span>{t('managePage.houseStatusPage.houseKeeping.fixRoom')}</span>
      </div>
    ),
    key: ACTION_BOOKING.FIX_ROOM,
    permission: PermissionActionTypes.SET_ROOM_STATUS
  }
];

export const getFoundColumns = (
  totalRemain: number,
  onChangeNumberRoom: (value: number, roomTypeId: number) => void,
  onChangePriceType: (value: string | number, roomTypeId: number) => void,
  onChangeEditPrice: (value: CheckboxChangeEvent, roomTypeId: number) => void,
  onChangePrice: (value: number | null, roomTypeId: number) => void,
  t: (key: string) => string
) => {
  const columnFoundRoom: TableProps<RoomType>['columns'] = [
    {
      title: t('common.bookingData.roomType'),
      dataIndex: 'room_type_name',
      key: 'room_type_name',
      width: '120px'
    },
    {
      title: `${t('common.bookingData.room')} (${totalRemain} ${t('common.bookingData.remaining')})`,
      dataIndex: 'available_rooms',
      key: 'available_rooms',
      render: (_, { total_rooms, available_rooms, room_type_id, used_rooms = 0 }) => (
        <div className="flex items-center" style={{ gap: '8px' }}>
          <InputNumber
            className="responsive-room-input"
            value={used_rooms}
            controls={false}
            min={0}
            max={available_rooms}
            onChange={value => onChangeNumberRoom(value || 0, room_type_id)}
            addonBefore={
              <MinusOutlined
                onClick={() => {
                  if (used_rooms === 0) {
                    return;
                  }
                  onChangeNumberRoom(used_rooms - 1, room_type_id);
                }}
              />
            }
            addonAfter={
              <PlusOutlined
                onClick={() => {
                  // if (used_rooms >= available_rooms) {
                  //   return;
                  // }
                  onChangeNumberRoom(used_rooms + 1, room_type_id);
                }}
              />
            }
          />
          <span
            className={classNames('flex-shrink-0', {
              required: available_rooms - (used_rooms || 0) < 0
            })}
          >
            {available_rooms - (used_rooms || 0)}/{total_rooms} {t('common.bookingData.remaining')}{' '}
            <span className="required">*</span>
          </span>
        </div>
      )
    },
    {
      title: t('common.bookingData.priceList'),
      dataIndex: 'pricing',
      key: 'pricing',
      width: '180px',
      render: (_, { pricing, room_type_id, pricelist_id }) => {
        return (
          <Select
            value={pricelist_id || ''}
            className="w-full"
            options={[
              {
                label: t('bookingPage.booking.default'),
                value: ''
              },
              ...pricing.map(item => {
                return {
                  label: item.pricelist_name,
                  value: item.pricelist_id
                };
              })
            ]}
            onChange={e => onChangePriceType(e, room_type_id)}
          />
        );
      }
    },
    {
      title: t('common.bookingData.defaultPrice'),
      dataIndex: 'price',
      key: 'price',
      width: '150px',
      align: 'right',
      render: (value, { pricing, pricelist_id, is_editable_price, room_type_id, edited_price }) => {
        if (is_editable_price) {
          return (
            <InputNumber
              value={edited_price !== undefined ? edited_price : value}
              formatter={value => formatInputNumber(value)}
              onChange={e => onChangePrice(e, room_type_id)}
              style={{ width: 110 }}
            />
          );
        }

        let renderPrice: number | undefined = value;
        if (pricelist_id) {
          renderPrice = pricing.find(item => item.pricelist_id === pricelist_id)?.price;
        }
        return <span>{renderPrice?.toLocaleString('vn')}</span>;
      }
    },
    {
      title: t('bookingPage.booking.priceEdit'),
      dataIndex: 'is_editable_price',
      key: 'is_editable_price',
      width: '120px',
      align: 'center',
      render: (value: boolean, { room_type_id }) => {
        return <Checkbox checked={value} onChange={e => onChangeEditPrice(e, room_type_id)} />;
      }
    },
    {
      title: t('bookingPage.booking.guest'),
      dataIndex: 'total_rooms',
      key: 'total_rooms',
      width: '160px',
      render: (_, { max_adult, max_child }) => (
        <div className="number-guest-cell">
          <div className="number-guest-cell__type">
            <UserOutlined style={{ fontSize: '20px', color: '#00000040' }} />
            <span>{max_adult || 0}</span>
          </div>
          <div className="number-guest-cell__type">
            <IconBaby style={{ width: '20px', height: '20px' }} />
            <span>{max_child || 0}</span>
          </div>
        </div>
      )
    }
  ];

  return columnFoundRoom;
};

export const getColumnCleaningDetail = (t: (key: string) => string): TableProps<any>['columns'] => [
  {
    title: t('common.bookingData.room'),
    dataIndex: 'roomId',
    key: 'roomId',
    width: '115px',
    align: 'center'
  },
  {
    title: t('common.customer.name'),
    dataIndex: 'customerName',
    key: 'customerName',
    width: '170px'
  },
  {
    title: t('common.bookingData.customerQty'),
    dataIndex: 'guests',
    key: 'guests',
    width: '110px'
  },
  {
    title: t('cashierPage.invoice.arrivalDate'),
    dataIndex: 'check_in',
    key: 'check_in',
    width: '120px'
  },
  {
    title: t('cashierPage.invoice.departureDate'),
    dataIndex: 'check_out',
    key: 'check_out',
    width: '120px'
  },
  {
    title: t('common.bookingData.services.service'),
    key: 'service',
    dataIndex: 'service',
    width: '120px'
  },
  {
    title: t('common.note'),
    key: 'note',
    dataIndex: 'note'
  }
];

export const fakeDataCleaningDetail: any[] = [
  {
    roomId: 101,
    customerName: 'Nguyễn Thu Hằng',
    guests: 2,
    check_in: '23/4',
    check_out: '26/4',
    service: 'ARR',
    note: ''
  },
  {
    roomId: 102,
    customerName: 'Lê Minh Kha',
    guests: 2,
    check_in: '23/4',
    check_out: '26/4',
    service: 'FS',
    note: ''
  },
  {
    roomId: 103,
    customerName: 'Phạm Phú Lam',
    guests: 1,
    check_in: '23/4',
    check_out: '24/4',
    service: 'ARR',
    note: ''
  },
  {
    roomId: 104,
    customerName: 'Minh Châu',
    guests: 3,
    check_in: '23/4',
    check_out: '27/4',
    service: 'FS',
    note: ''
  }
];

export const getStayingGuestsColumns = (
  onEditCustomer: (id: number) => void,
  page: number,
  t: (key: string) => string
) => {
  const columnStayingGuest: TableProps<any>['columns'] = [
    {
      title: t('common.no'),
      dataIndex: 'id',
      key: 'id',
      width: '100px',
      align: 'center',
      render: (value, _, index: number) => (page - 1) * PAGE_SIZE_STAYING_GUESTS + index + 1
    },
    {
      title: t('common.customer.fullName'),
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: t('common.customer.dob'),
      dataIndex: 'dob',
      key: 'dob',
      width: '110px',
      render: (value: string) => dayjs(value).format('DD/MM/YYYY')
    },
    {
      title: t('common.customer.phone'),
      dataIndex: 'phone',
      key: 'phone',
      width: '120px'
    },
    {
      title: t('common.customer.gender'),
      dataIndex: 'gender',
      key: 'gender',
      width: '100px',
      align: 'center',
      render: (value: string) => MAP_GENDER[value]
    },
    {
      title: t('common.customer.nationality'),
      key: 'country',
      dataIndex: 'country',
      width: '120px',
      render: (country: CountryType) => country.name
    },
    {
      title: t('common.customer.identification'),
      key: 'identification',
      dataIndex: 'identification'
    },
    {
      title: t('common.bookingData.room'),
      key: 'room_name',
      dataIndex: 'room_name',
      width: '120px'
    },
    {
      title: t('common.statuses.checkInDate'),
      key: 'check_in',
      dataIndex: 'check_in',
      render: (value: string) => toLocalTime(value, 'DD/MM/YYYY')
    },
    {
      title: t('common.statuses.checkOutDate'),
      key: 'check_out',
      dataIndex: 'check_out',
      render: (value: string) => toLocalTime(value, 'DD/MM/YYYY')
    },
    {
      title: '',
      key: '',
      dataIndex: '',
      width: '60px',
      render: (_, { id }) => (
        <Button
          type="link"
          icon={<EditFilled style={{ color: 'rgba(0,0,0,0.54)' }} />}
          onClick={() => onEditCustomer(id)}
        ></Button>
      )
    }
  ];
  return columnStayingGuest;
};

export const getColumnNationalityStatistic = (t: (key: string) => string) => {
  const columnNationalityStatistic: TableProps<NationalityStatisticType>['columns'] = [
    {
      title: t('managePage.nationalityStatisticPage.nationalityId'),
      key: 'country_id',
      dataIndex: 'country_id',
      width: '90px',
      align: 'center'
    },
    {
      title: t('managePage.nationalityStatisticPage.countryName'),
      key: 'country_name',
      dataIndex: 'country_name',
      width: '90px',
      align: 'center'
    },
    {
      title: t('managePage.nationalityStatisticPage.totalStayingGuests'),
      dataIndex: 'count',
      key: 'count',
      width: '90px',
      align: 'center'
    },
    {
      title: t('common.customer.male'),
      dataIndex: 'male',
      key: 'male',
      width: '70px',
      align: 'center',
      render: (_, values) => (values ? values.gender.male : 0)
    },
    {
      title: t('common.customer.female'),
      dataIndex: 'female',
      key: 'female',
      width: '70px',
      align: 'center',
      render: (_, values) => (values ? values.gender.female : 0)
    }
  ];
  return columnNationalityStatistic;
};

export const getCashFlowColumns = (
  page: number,
  t: (key: string) => string,
  onClickEdit?: (paymentId: number) => void,
  paymentId?: number,
  setNote?: (note: string) => void,
  setExpenseType?: (type: string) => void,
  setOutcomeAmount?: (amount: number | undefined) => void,
  completeEdit?: (finishEdit: boolean) => void,
  onClickDelete?: (paymentId: number) => void
): TableProps<any>['columns'] => {
  const EXPENSE_TYPE_OPTIONS = getExpenseTypeOptions(t);

  return [
    {
      title: t('common.no'),
      dataIndex: 'index',
      key: 'index',
      width: '100px',
      align: 'center',
      render: (__, _, index: number) => (page - 1) * PAGE_SIZE_CASH_FLOW + index + 1
    },
    {
      title: t('common.bookingData.bookingId'),
      dataIndex: 'booking_id',
      key: 'booking_id',
      align: 'center',
      width: '120px',
      render: renderWithLineThrough
    },
    {
      title: t('managePage.cashBookDetailPage.paymentDate'),
      dataIndex: 'payment_date',
      key: 'payment_date',
      width: '170px',
      render: (value, record) => renderWithLineThrough(toLocalTime(value, 'DD-MM-YYYY'), record)
    },
    {
      title: t('managePage.cashBookDetailPage.incomeId'),
      dataIndex: 'id',
      key: 'id',
      width: '120px',
      render: (value, data) =>
        data.payment_type === CASHFLOW_TYPE.INCOME && (
          <span
            style={{ color: '#4CAF50' }}
            className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}
          >
            PTM{value}
          </span>
        )
    },
    {
      title: t('managePage.cashBookDetailPage.expenseId'),
      dataIndex: 'id',
      key: 'id',
      width: '120px',
      render: (value, data) =>
        data.payment_type === CASHFLOW_TYPE.PAYMENT && (
          <span
            style={{ color: 'rgb(255,87,34)' }}
            className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}
          >
            PCTM{value}
          </span>
        )
    },
    {
      title: t('managePage.cashBookDetailPage.expenseType'),
      dataIndex: 'expense_type',
      key: 'expense_type',
      width: '200px',
      render: (data: string, record: any) => {
        if (record.payment_type === 'income') {
          return record.description;
        }
        if (record.id === paymentId) {
          const selectedOption = EXPENSE_TYPE_OPTIONS.find(option => option.value === data);

          return (
            <Select
              defaultValue={selectedOption?.label}
              onChange={value => setExpenseType?.(value)}
              options={EXPENSE_TYPE_OPTIONS.map(option => ({
                label: option.label,
                value: option.value
              }))}
              style={{ width: '100%' }}
            />
          );
        } else {
          const option = EXPENSE_TYPE_OPTIONS.find(option => option.value === data);
          return option
            ? renderWithLineThrough(option.label, record)
            : renderWithLineThrough(data, record);
        }
      }
    },
    {
      title: t('common.note'),
      dataIndex: 'note',
      key: 'note',
      width: '250px',
      render: (text, record) => {
        return record.id === paymentId ? (
          <Input
            defaultValue={text ? text : ''}
            onChange={e => setNote?.(e.target.value)}
            style={{ width: '100%' }}
            autoFocus
          />
        ) : (
          renderWithLineThrough(text, record)
        );
      }
    },
    {
      title: t('managePage.cashBookDetailPage.incomeContent'),
      width: '120px',
      align: 'center',
      children: [
        {
          title: t('common.currency'),
          key: 'income',
          dataIndex: 'income',
          width: '120px',
          align: 'center',
          className: 'income-cell',
          render: (_, data) => {
            if (!data || !data.payment_type || data.payment_type === CASHFLOW_TYPE.PAYMENT)
              return 0;
            return (
              <span
                className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}
              >
                {data.amount?.toLocaleString('vn')}
              </span>
            );
          }
        }
      ]
    },
    {
      title: t('managePage.cashBookDetailPage.expenseContent'),
      width: '120px',
      align: 'center',
      children: [
        {
          title: t('common.currency'),
          key: 'outcome',
          dataIndex: 'outcome',
          width: '120px',
          align: 'center',
          className: 'outcome-cell',
          render: (_, record) => {
            if (!record.payment_type || record.payment_type === CASHFLOW_TYPE.INCOME) return 0;

            if (record.id === paymentId) {
              return (
                <InputNumber
                  defaultValue={record.amount?.toLocaleString('vn')}
                  onChange={value => setOutcomeAmount?.(-Math.abs(Number(value)))}
                  style={{ width: '100%' }}
                  autoFocus
                  formatter={value => formatInputMoney(-Math.abs(Number(value)))}
                />
              );
            }
            return (
              <span
                className={classNames({
                  'line-through': record.state === PAYMENT_STATUS.CANCELLED
                })}
              >
                {Math.abs(record.amount)?.toLocaleString('vn') || 0}
              </span>
            );
          }
        }
      ]
    },
    {
      title: t('managePage.cashBookDetailPage.userName'),
      key: 'user_name',
      dataIndex: 'user_name',
      align: 'center',
      render: renderWithLineThrough
    },

    {
      title: '',
      dataIndex: '',
      key: 'actions',
      width: '110px',
      render: (_: any, data: CashFlowType) =>
        data.id === paymentId &&
        data.payment_type != PAYMENT_TYPE.INCOME &&
        data.state !== PAYMENT_STATUS.CANCELLED ? (
          <Button onClick={() => completeEdit?.(true)} style={{ padding: '4px 8px' }}>
            <CheckOutlined />
          </Button>
        ) : (
          onClickEdit &&
          data.payment_type != PAYMENT_TYPE.INCOME &&
          data.state !== PAYMENT_STATUS.CANCELLED && (
            <>
              {data.expense_type ? (
                <Button
                  onClick={() => onClickEdit(data.id)}
                  style={{ padding: '4px 8px', marginRight: '4px' }}
                >
                  <EditFilled />
                </Button>
              ) : (
                []
              )}
              {data.expense_type ? (
                <IBButton
                  permission={PermissionActionTypes.INCOME_EXPENSE_DELETE}
                  onClick={() => onClickDelete?.(data.id)}
                  style={{ padding: '4px 8px' }}
                >
                  <DeleteOutlined />
                </IBButton>
              ) : (
                []
              )}
            </>
          )
        )
    }
  ];
};

export const getActionNightAuditStep0 = (t: (key: string) => string): CustomMenuItem[] => [
  {
    label: t('common.actions.cancelAction'),
    key: ACTION_BOOKING.CANCEL_BOOKING
  },
  {
    label: t('common.statuses.checkInRoom'),
    key: ACTION_BOOKING.GET_ROOM,
    permission: PermissionActionTypes.CHECK_IN
  },
  {
    label: t('common.actions.changeDate'),
    key: ACTION_BOOKING.CHANGE_DATE,
    permission: PermissionActionTypes.CHANGE_DATE
  }
];
export const getActionNightAuditStep1 = (t: (key: string) => string): CustomMenuItem[] => [
  {
    label: t('bookingPage.roomMap.checkOutRoom'),
    key: ACTION_BOOKING.CHECK_OUT,
    permission: PermissionActionTypes.CHECK_OUT
  },
  {
    label: t('common.actions.payment'),
    key: ACTION_BOOKING.RESOLVE_DOCUMENT
  }
];

export const getActionNightAuditStep2 = (t: (key: string) => string): CustomMenuItem[] => [
  {
    label: t('common.actions.postRoomCharge'),
    key: ACTION_BOOKING.POST_ROOM_CHARGE,
    permission: PermissionActionTypes.POST_ROOM_CHARGE
  }
];

const menuAction: any = {
  0: getActionNightAuditStep0,
  1: getActionNightAuditStep1,
  2: getActionNightAuditStep2
};

export const getNightAuditColumnStep0And1 = (
  onClick: MenuProps['onClick'],
  step: number,
  t: (key: string) => string
) => {
  const menu = menuAction[step](t);
  const statusMap = getNightAuditStatusMap(t); // ✅ Now valid here

  const columnNightAudit: TableProps<any>['columns'] = [
    {
      title: t('common.bookingData.bookingId'),
      dataIndex: 'booking_id',
      key: 'booking_id',
      width: '140px',
      render: value => `#${value}`
    },
    {
      title: t('common.customer.name'),
      dataIndex: 'partner_name',
      key: 'partner_name'
    },
    {
      title: t('common.bookingData.room'),
      dataIndex: 'room_name',
      key: 'room_name',
      render: (value, record) => value || record.room_type_name
    },
    {
      title: t('common.bookingData.priceList'),
      dataIndex: 'pricelist',
      key: 'pricelist',
      width: '120px',
      render: value => value?.name || t('common.bookingData.defaultPrice')
    },
    {
      title: t('common.bookingData.status'),
      dataIndex: 'status',
      key: 'status',
      width: '130px',
      render: value => <span style={{ color: '#9C4F3B' }}>{statusMap[value] || ''}</span>
    },
    {
      title: t('bookingPage.bookingList.source'),
      width: '120px',
      dataIndex: 'source_name',
      key: 'source_name',
      render: value => (value ? value : '-')
    },
    {
      title: t('common.time.checkIn'),
      width: '140px',
      dataIndex: 'check_in',
      key: 'check_in',
      render: value => toLocalTime(value, 'DD/MM/YYYY')
    },
    {
      title: t('common.time.checkOut'),
      width: '140px',
      dataIndex: 'check_out',
      key: 'check_out',
      render: value => toLocalTime(value, 'DD/MM/YYYY')
    },
    {
      title: t('common.bookingData.services.totalAmount'),
      key: 'total_price',
      dataIndex: 'total_price',
      width: '140px',
      render: value => Number(value).toLocaleString('vn')
    },
    ...(step !== 2
      ? [
          {
            title: t('common.bookingData.remainAmount'),
            key: 'remain',
            dataIndex: 'remain',
            width: '200px',
            render: (_: any, record: any) => (
              <span>{record.remain_amount.toLocaleString('vn')}</span>
            )
          }
        ]
      : []),
    // {
    //   title: "Tiền đặt trước",
    //   key: "reserve",
    //   dataIndex: "reserve",
    //   width: "140px",
    //   render: (value) => value,
    // },
    {
      title: '',
      key: 'id',
      dataIndex: 'id',
      width: '100px',
      render: (_, record) => (
        <Dropdown
          trigger={['click']}
          menu={{
            // eslint-disable-next-line react-hooks/rules-of-hooks
            items: menu.filter((item: any) => {
              if (item.key === ACTION_BOOKING.CHECK_OUT && record.remain_amount !== 0) {
                return false;
              }
              return !(item.key === ACTION_BOOKING.RESOLVE_DOCUMENT && record.remain_amount === 0);
            }),
            onClick
          }}
          placement="bottom"
          overlayClassName="night-audit-menu"
          destroyPopupOnHide
        >
          <Button icon={<IconMore />} type="text" />
        </Dropdown>
      )
    }
  ];

  return columnNightAudit;
};

export const getColumnRoomLock = (
  t: (key: string) => string,
  getReport?: boolean,
  onClickEdit?: (id: number) => void
) => {
  const columnRoomLock: TableProps<RoomLockType>['columns'] = [
    // {
    //   title: 'ID',
    //   dataIndex: 'room_id',
    //   align: 'center',
    //   key: 'room_id',
    //   width: '80px'
    // },
    {
      title: t('bookingPage.roomLock.roomType'),
      dataIndex: 'room_type_name',
      align: 'left' as const,
      key: 'room_type_name',
      width: '110px'
    },
    {
      title: t('bookingPage.roomLock.roomNo'),
      dataIndex: 'room_no',
      align: 'left' as const,
      key: 'room_no',
      width: '100px',
      render: (value, record) => _.get(record, 'attributes.room_no', '')
    },
    {
      title: t('common.time.startDate'),
      dataIndex: 'start_date',
      align: 'left' as const,
      key: 'start_date',
      width: '160px',
      render: (value: string) => (value ? toLocalTime(value, 'DD/MM/YYYY HH:mm') : '')
    },
    {
      title: t('common.time.endDate'),
      dataIndex: getReport ? 'original_end_date' : 'end_date',
      align: 'left' as const,
      key: getReport ? 'original_end_date' : 'end_date',
      width: '160px',
      render: (value: string) => (value ? toLocalTime(value, 'DD/MM/YYYY HH:mm') : '')
    },
    ...(getReport
      ? [
          {
            title: t('managePage.roomLockHistory.openedDate'),
            dataIndex: 'end_date',
            align: 'left' as const,
            key: 'end_date',
            width: '100px',
            render: (value: string) => (value ? toLocalTime(value, 'DD/MM/YYYY HH:mm') : '')
          }
        ]
      : []),
    {
      title: t('common.note'),
      dataIndex: 'reason',
      align: 'left' as const,
      key: 'reason',
      width: '220px'
    },
    {
      title: t('bookingPage.roomLock.createDate'),
      dataIndex: 'create_date',
      align: 'left' as const,
      key: 'create_date',
      width: '160px',
      render: (value: string) => (value ? toLocalTime(value, 'DD/MM/YYYY') : '')
    },
    {
      title: t('bookingPage.roomLock.createUserName'),
      dataIndex: 'create_username',
      align: 'left' as const,
      key: 'create_username',
      width: '140px'
    },
    ...(onClickEdit
      ? [
          {
            title: '',
            dataIndex: '',
            key: 'actions',
            width: '80px',
            render: (_: any, data: RoomLockType) => {
              if (String(data.id) === 'root') {
                return null;
              }

              return (
                <Button
                  onClick={() => onClickEdit && onClickEdit(data.id)}
                  style={{ padding: '4px 8px' }}
                >
                  <EditFilled />
                </Button>
              );
            }
          }
        ]
      : [])
  ];

  return columnRoomLock;
};

export const getColumnBreakfast = (t: (key: string) => string) => {
  const columnBreakfasts: TableProps<any>['columns'] = [
    {
      title: t('common.bookingData.bookingId'),
      dataIndex: 'booking_id',
      key: 'booking_id',
      align: 'center',
      width: '80px',
      render: (value: string, row: any) => renderWithRowSpan(value, row, 'rowSpan')
    },
    {
      title: t('common.customer.name'),
      dataIndex: 'guest',
      key: 'guest',
      width: '180px'
    },
    {
      title: t('common.bookingData.room'),
      dataIndex: 'room_name',
      key: 'room_name',
      align: 'center',
      width: '90px',
      render: (value: string, row: any) => renderWithRowSpan(value, row, 'rowSpan')
    },
    {
      title: t('common.customer.adult'),
      dataIndex: 'adult',
      key: 'adult',
      align: 'center',
      width: '60px',
      render: (value: string, row: any) => renderWithRowSpan(value, row, 'rowSpan')
    },
    {
      title: t('common.customer.child'),
      dataIndex: 'child',
      key: 'child',
      align: 'center',
      width: '50px',
      render: (value: string, row: any) => renderWithRowSpan(value, row, 'rowSpan')
    },
    {
      title: t('common.time.checkIn'),
      dataIndex: 'arrival',
      key: 'arrival',
      align: 'center',
      width: '180px'
    },
    {
      title: t('common.time.checkOut'),
      dataIndex: 'departure',
      key: 'departure',
      align: 'center',
      width: '180px'
    }
  ];
  return columnBreakfasts;
};

export const getColumnTransactions = (
  t: (key: string) => string,
  paymentMethodNames: Record<string, string>
): TableProps<AllPaymentItem>['columns'] => {
  return [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '80px',
      render: (value, data) => (
        <span className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}>
          {value}
        </span>
      )
    },
    {
      title: t('common.bookingData.bookingId'),
      dataIndex: 'booking_id',
      key: 'booking_id',
      width: '80px',
      render: (value, data) => (
        <span className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}>
          {value}
        </span>
      )
    },
    {
      title: t('managePage.transactionPage.common.paymentDate'),
      dataIndex: 'payment_date',
      key: 'payment_date',
      width: '120px',
      render: (value, data) => (
        <span className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}>
          {toLocalTime(value, 'DD/MM/YYYY HH:mm')}
        </span>
      )
    },
    {
      title: t('common.note'),
      dataIndex: 'note',
      key: 'note',
      width: '140px',
      render: (value, data) => (
        <span className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}>
          {value}
        </span>
      )
    },
    {
      title: t('managePage.transactionPage.common.amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: '150px',
      render: (value, data) => (
        <span className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}>
          {formatCurrency(value)}
        </span>
      )
    },
    {
      title: t('managePage.transactionPage.common.paymentType'),
      dataIndex: 'payment_type',
      key: 'payment_type',
      width: '50px',
      render: (value, data) => (
        <span className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}>
          {getMapPaymentType(t)[value]}
        </span>
      )
    },
    {
      title: t('managePage.transactionPage.common.paymentMethod'),
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: '120px',
      render: (value, data) => (
        <span className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}>
          {paymentMethodNames?.[value]}
        </span>
      )
    },
    {
      title: t('managePage.transactionPage.common.userName'),
      dataIndex: 'user_name',
      key: 'user_name',
      width: '100px',
      render: (value, data) => (
        <span className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}>
          {value}
        </span>
      )
    },
    {
      title: t('common.customer.name'),
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: '100px',
      render: (value, data) => (
        <span className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}>
          {value}
        </span>
      )
    },
    {
      title: t('managePage.transactionPage.common.desc'),
      dataIndex: 'description',
      key: 'description',
      width: '200px',
      render: (value, data) => (
        <span className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}>
          {value}
        </span>
      )
    }
  ];
};

export const getColumnTransactionsV2 = (t: (key: string) => string, isAcc?: boolean) => {
  const columnTransactionsV2: TableProps<AllPaymentItem>['columns'] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '60px',
      render: (value, data) => (
        <span
          className={classNames({
            'line-through': data.state === PAYMENT_STATUS.CANCELLED
          })}
        >
          {value}
        </span>
      )
    },
    {
      title: t('common.bookingData.bookingId'),
      dataIndex: 'booking_id',
      key: 'booking_id',
      width: '70px',
      render: (value, data) => (
        <span
          className={classNames({
            'line-through': data.state === PAYMENT_STATUS.CANCELLED
          })}
        >
          {value}
        </span>
      )
    },
    {
      title: t('common.bookingData.originalBookingId'),
      dataIndex: 'original_booking_id',
      key: 'original_booking_id',
      width: '90px',
      render: (value: string, data: AllPaymentItem) => (
        <span className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}>
          {value}
        </span>
      )
    },
    ...(isAcc
      ? [
          {
            title: t('common.bookingData.checkInDate'),
            dataIndex: 'checkin_date',
            key: 'checkin_date',
            width: '90px',
            render: (value: string, data: AllPaymentItem) => (
              <span
                className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}
              >
                {toLocalTime(value, 'DD/MM/YYYY')}
              </span>
            )
          },
          {
            title: t('common.bookingData.checkOutDate'),
            dataIndex: 'checkout_date',
            key: 'checkout_date',
            width: '90px',
            render: (value: string, data: AllPaymentItem) => (
              <span
                className={classNames({
                  'line-through': data.state === PAYMENT_STATUS.CANCELLED
                })}
              >
                {toLocalTime(value, 'DD/MM/YYYY')}
              </span>
            )
          },
          {
            title: t('cashierPage.transactionSearch.taCompany'),
            dataIndex: 'travel_agency_name',
            key: 'travel_agency_name',
            width: '100px',
            render: (value: string, data: AllPaymentItem) => (
              <span
                className={classNames({
                  'line-through': data.state === PAYMENT_STATUS.CANCELLED
                })}
              >
                {value}
              </span>
            )
          }
        ]
      : []),
    {
      title: t('managePage.transactionPage.common.paymentDate'),
      dataIndex: 'payment_date',
      key: 'payment_date',
      width: '90px',
      render: (value, data) => (
        <span
          className={classNames({
            'line-through': data.state === PAYMENT_STATUS.CANCELLED
          })}
        >
          {toLocalTime(value, 'DD/MM/YYYY')}
        </span>
      )
    },
    {
      title: t('common.note'),
      dataIndex: 'note',
      key: 'note',
      width: '200px',
      render: (value, data) => (
        <span
          className={classNames({
            'line-through': data.state === PAYMENT_STATUS.CANCELLED
          })}
        >
          {value}
        </span>
      )
    },
    {
      title: t('managePage.transactionPage.common.amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: '110px',
      render: (value, data) => (
        <span
          className={classNames({
            'line-through': data.state === PAYMENT_STATUS.CANCELLED
          })}
        >
          {formatCurrency(value)}
        </span>
      )
    },
    {
      title: t('managePage.transactionPage.common.paymentType'),
      dataIndex: 'payment_type',
      key: 'payment_type',
      width: '70px',
      render: (value, data) => (
        <span
          className={classNames({
            'line-through': data.state === PAYMENT_STATUS.CANCELLED
          })}
        >
          {value.charAt(0).toUpperCase() + value.slice(1)}
        </span>
      )
    },
    {
      title: t('managePage.transactionPage.common.paymentMethod'),
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: '100px',
      render: (value, data) => (
        <span
          className={classNames({
            'line-through': data.state === PAYMENT_STATUS.CANCELLED
          })}
        >
          {value}
        </span>
      )
    },
    ...(isAcc
      ? [
          {
            title: 'SO',
            dataIndex: 'sale_order_name',
            key: 'sale_order_name',
            width: '80px',
            render: (value: string, data: AllPaymentItem) => (
              <span
                className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}
              >
                {value}
              </span>
            )
          },
          {
            title: t('managePage.transactionPage.acc.paymentStatus'),
            dataIndex: 'payment_status',
            key: 'payment_status',
            width: '90px',
            render: (value: string, data: AllPaymentItem) => {
              const getPaymentStatusColor = (status: string) => {
                switch (status?.toLowerCase()) {
                  case 'stayed':
                    return '#52c41a'; // green
                  case 'in_house':
                    return '#d4af37'; // brownish yellow
                  case 'deposit':
                    return '#1890ff'; // blue
                  case 'invoiced':
                    return '#ff4d4f'; // red
                  default:
                    return 'inherit';
                }
              };

              return (
                <span
                  className={classNames({
                    'line-through': data.state === PAYMENT_STATUS.CANCELLED
                  })}
                  style={{
                    color: getPaymentStatusColor(value),
                    fontWeight: '500'
                  }}
                >
                  {PAYMENT_STATUS_ACC_NAMES[value as keyof typeof PAYMENT_STATUS_ACC_NAMES]}
                </span>
              );
            }
          }
        ]
      : []),
    {
      title: t('managePage.transactionPage.common.userName'),
      dataIndex: 'user_name',
      key: 'user_name',
      width: '80px',
      render: (value, data) => (
        <span
          className={classNames({
            'line-through': data.state === PAYMENT_STATUS.CANCELLED
          })}
        >
          {value}
        </span>
      )
    },
    {
      title: t('common.customer.name'),
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: '90px',
      render: (value, data) => (
        <span
          className={classNames({
            'line-through': data.state === PAYMENT_STATUS.CANCELLED
          })}
        >
          {value}
        </span>
      )
    },
    {
      title: t('common.bookingData.debitPartner'),
      dataIndex: 'debit_partner_name',
      key: 'debit_partner_name',
      width: '100px',
      render: (value: string, data: AllPaymentItem) => (
        <span className={classNames({ 'line-through': data.state === PAYMENT_STATUS.CANCELLED })}>
          {value}
        </span>
      )
    },
    {
      title: t('managePage.transactionPage.common.desc'),
      dataIndex: 'description',
      key: 'description',
      width: '150px',
      render: (value, data) => (
        <span
          className={classNames({
            'line-through': data.state === PAYMENT_STATUS.CANCELLED
          })}
        >
          {value}
        </span>
      )
    },
    {
      title: t('managePage.transactionPage.v2.createdDate'),
      dataIndex: 'created_date',
      key: 'created_date',
      width: '100px',
      render: (value, data) => (
        <span
          className={classNames({
            'line-through': data.state === PAYMENT_STATUS.CANCELLED
          })}
        >
          {toLocalTime(value, 'DD/MM/YYYY HH:mm:ss')}
        </span>
      )
    }
  ];
  return columnTransactionsV2;
};

export const getColumnReservationForecast = (t: (key: string) => string) => {
  const columnReservationForecast: TableProps<any>['columns'] = [
    {
      title: t('managePage.reservationForecastPage.date'),
      dataIndex: 'date',
      key: 'date',
      width: '60px'
    },
    {
      title: t('managePage.reservationForecastPage.checkIn.title'),
      align: 'center',
      children: [
        {
          title: t('managePage.reservationForecastPage.checkIn.checkInRoom'),
          key: 'check_in_room',
          dataIndex: ['check_in', 'room'],
          width: '60px',
          align: 'center',
          render: formatNumber
        },
        {
          title: t('managePage.reservationForecastPage.checkIn.checkInGuest'),
          key: 'check_in_guest',
          dataIndex: ['check_in', 'pax'],
          width: '60px',
          align: 'center',
          render: formatNumber
        }
      ]
    },
    {
      title: t('managePage.reservationForecastPage.checkOut.title'),
      align: 'center',
      children: [
        {
          title: t('managePage.reservationForecastPage.checkOut.checkOutRoom'),
          key: 'check_out_room',
          dataIndex: ['check_out', 'room'],
          width: '60px',
          align: 'center',
          render: formatNumber
        },
        {
          title: t('managePage.reservationForecastPage.checkOut.checkOutGuest'),
          key: 'check_out_guest',
          dataIndex: ['check_out', 'pax'],
          width: '60px',
          align: 'center',
          render: formatNumber
        }
      ]
    },
    {
      title: t('managePage.reservationForecastPage.roomsLocked.title'),
      align: 'center',
      children: [
        {
          title: t('managePage.reservationForecastPage.roomsLocked.roomsLocked'),
          key: 'rooms_locked',
          dataIndex: ['rooms_locked'],
          width: '60px',
          align: 'center',
          render: formatNumber
        }
      ]
    },
    {
      title: t('managePage.reservationForecastPage.occupied.title'),
      align: 'center',
      children: [
        {
          title: t('managePage.reservationForecastPage.occupied.occRoom'),
          key: 'occupied_room',
          dataIndex: ['occupied', 'room'],
          width: '60px',
          align: 'center',
          render: formatNumber
        },
        {
          title: t('managePage.reservationForecastPage.occupied.occPercent'),
          key: 'occupied_percent',
          dataIndex: ['occupied', 'occupied_percent'],
          width: '60px',
          align: 'center',
          render: value => {
            return value !== '' ? `${Number(value)}%` : '';
          }
        },
        {
          title: t('managePage.reservationForecastPage.occupied.occGuest'),
          key: 'occupied_guest',
          dataIndex: ['occupied', 'pax'],
          width: '60px',
          align: 'center',
          render: formatNumber
        }
      ]
    },
    {
      title: t('managePage.reservationForecastPage.financials.title'),
      align: 'center',
      children: [
        {
          title: t('managePage.reservationForecastPage.financials.revenue'),
          key: 'revenue',
          dataIndex: ['financials', 'revenue'],
          width: '60px',
          align: 'center',
          render: value => formatNumber(Math.round(value))
        },
        {
          title: t('managePage.reservationForecastPage.financials.adr'),
          key: 'gross_profit',
          dataIndex: ['financials', 'gross_profit'],
          width: '60px',
          align: 'center',
          render: value => formatNumber(Math.round(value))
        },
        {
          title: t('managePage.reservationForecastPage.financials.netProfit'),
          key: 'net_profit',
          dataIndex: ['financials', 'net_profit'],
          width: '60px',
          align: 'center',
          render: value => formatNumber(Math.round(value))
        }
      ]
    }
  ];
  return columnReservationForecast;
};

export const getColumnShiftHandover = (t: (key: string) => string) => {
  const columnShiftHandover: TableProps<ShiftHandover>['columns'] = [
    {
      title: t('managePage.shiftHandoverListPage.shift'),
      dataIndex: 'id',
      key: 'id',
      width: '40px'
    },
    {
      title: t('managePage.shiftHandoverListPage.openingTime'),
      dataIndex: 'opening_time',
      key: 'opening_time',
      width: '70px',
      render(value) {
        return toLocalTime(value);
      }
    },
    {
      title: t('managePage.shiftHandoverListPage.closingTime'),
      dataIndex: 'closing_time',
      key: 'closing_time',
      width: '70px',
      render(value) {
        return value ? toLocalTime(value) : '';
      }
    },
    {
      title: t('managePage.shiftHandoverListPage.userName'),
      dataIndex: 'user_name',
      key: 'user_name',
      width: '80px'
    },
    {
      title: t('managePage.shiftHandoverListPage.handoverUserName'),
      dataIndex: 'handover_user_name',
      key: 'handover_user_name',
      width: '80px'
    },
    {
      title: t('managePage.shiftHandoverListPage.openingAmount'),
      dataIndex: 'opening_amount',
      key: 'opening_amount',
      width: '50px',
      render: formatMoneyValue
    },
    {
      title: t('managePage.shiftHandoverListPage.incomeAmount'),
      dataIndex: 'income_amount',
      key: 'income_amount',
      width: '50px',
      render: formatMoneyValue
    },
    {
      title: t('managePage.shiftHandoverListPage.expenseAmount'),
      dataIndex: 'expense_amount',
      key: 'expense_amount',
      width: '50px',
      render: formatMoneyValue
    },
    {
      title: t('managePage.shiftHandoverListPage.currentAmount'),
      dataIndex: 'current_amount',
      key: 'current_amount',
      width: '60px',
      render: formatMoneyValue
    },
    {
      title: t('managePage.shiftHandoverListPage.actualAmount'),
      dataIndex: 'actual_amount',
      key: 'actual_amount',
      width: '60px',
      render: formatMoneyValue
    },
    {
      title: t('managePage.shiftHandoverListPage.difference_amount'),
      dataIndex: 'difference_amount',
      key: 'difference_amount',
      width: '70px',
      render: formatMoneyValue
    },
    {
      title: t('common.note'),
      dataIndex: 'note',
      key: 'note',
      width: '50px'
    }
  ];
  return columnShiftHandover;
};

export const getColumnWarehouseManagement = (t: (key: string) => string) => {
  const columnsWarehouseManagement: TableProps<WarehouseProductsType>['columns'] = [
    {
      title: t('managePage.inventoryManagementPage.productId'),
      dataIndex: 'product_id',
      key: 'product_id',
      width: '40px'
    },
    {
      title: t('managePage.inventoryManagementPage.productName'),
      dataIndex: 'product',
      key: 'product',
      width: '200px'
    },
    {
      title: t('managePage.inventoryManagementPage.reservedQty'),
      dataIndex: 'reserved_qty',
      key: 'reserved_qty',
      width: '200px'
    },
    {
      title: t('managePage.inventoryManagementPage.freeQty'),
      dataIndex: 'free_qty',
      key: 'free_qty',
      width: '200px'
    }
  ];
  return columnsWarehouseManagement;
};

export const getColumnTravelAgency = (t: (key: string) => string) => {
  const columnTravelAgency: TableProps<TravelAgencyResponse>['columns'] = [
    {
      title: t('managePage.travelAgencyPage.taId'),
      dataIndex: 'id',
      key: 'id',
      width: '60px'
    },
    {
      title: t('managePage.travelAgencyPage.taName'),
      dataIndex: 'name',
      key: 'name',
      width: '260px'
    },
    {
      title: t('managePage.travelAgencyPage.taType'),
      render: ({ is_travel_agency, is_company }) =>
        [is_travel_agency && 'TA', is_company && 'Company'].filter(Boolean).join(', '),
      width: '110px'
    },
    {
      title: t('managePage.travelAgencyPage.invoiceName'),
      dataIndex: 'invoice_name',
      key: 'invoice_name',
      width: '220px'
    },
    {
      title: t('managePage.travelAgencyPage.taEmail'),
      dataIndex: 'email',
      key: 'email',
      width: '150px'
    },
    {
      title: t('managePage.travelAgencyPage.taVat'),
      dataIndex: 'vat',
      key: 'vat',
      width: '100px'
    },
    {
      title: t('managePage.travelAgencyPage.externalId'),
      dataIndex: 'external_id',
      key: 'external_id',
      width: '100px'
    },
    {
      title: t('managePage.travelAgencyPage.sourceName'),
      dataIndex: 'medium_name',
      key: 'medium_name',
      width: '100px'
    },
    {
      title: t('managePage.travelAgencyPage.marketSegmentName'),
      dataIndex: 'source_name',
      key: 'source_name',
      width: '100px'
    },
    {
      title: t('managePage.travelAgencyPage.taNationality'),
      dataIndex: 'country_name',
      key: 'country_name',
      width: '90px'
    },
    {
      title: t('managePage.travelAgencyPage.taAddress'),
      dataIndex: 'address',
      key: 'address',
      width: '120px',
      render: renderTextWithEllipsis
    },
    {
      title: t('common.note'),
      dataIndex: 'notes',
      key: 'notes',
      width: '300px',
      render: renderTextWithEllipsis
    }
  ];
  return columnTravelAgency;
};

// List group
export const getColumnListGroup = (page: number, t: (key: string) => string) => {
  const columnListGroup: TableProps<ResponseGroupBookings>['columns'] = [
    {
      title: t('common.no'),
      dataIndex: 'stt',
      key: 'stt',
      width: '60px',
      align: 'center',
      render: (value, _, index: number) => (page - 1) * PAGE_SIZE_STAYING_GUESTS + index + 1
    },
    // {
    //   title: 'Confirm#',
    //   dataIndex: 'confirm',
    //   key: 'confirm',
    //   width: '150px',
    //   align: 'center'
    // },
    {
      title: t('groupPage.groupList.groupCode'),
      dataIndex: 'id',
      key: 'id',
      width: '80px',
      align: 'center'
    },
    {
      title: t('groupPage.groupList.groupName'),
      dataIndex: 'name',
      key: 'group_name',
      width: '250px',
      render: renderTextWithEllipsis
    },
    {
      title: t('common.bookingData.status'),
      dataIndex: 'state',
      key: 'state',
      width: '80px',
      render: (value: keyof typeof LIST_GROUP_STATUS_NAMES) => LIST_GROUP_STATUS_NAMES[value]
    },
    {
      title: t('groupPage.groupList.taName'),
      dataIndex: 'hotel_travel_agency_name',
      key: 'hotel_travel_agency_name',
      width: '250px',
      render: renderTextWithEllipsis
    },
    {
      title: t('groupPage.createGroup.source'),
      dataIndex: 'source_name',
      key: 'source_name',
      width: '80px'
    },
    {
      title: t('groupPage.createGroup.vipLevel'),
      dataIndex: 'vip_level',
      key: 'vip_level',
      width: '80px',
      render: (value: string) => getVipLevelMap(t)[value]
    },
    {
      title: t('groupPage.createGroup.firstArrival'),
      dataIndex: 'first_arrival',
      key: 'first_arrival',
      width: '150px',
      align: 'center',
      render: (value: string) => toLocalTime(value, 'DD/MM/YYYY')
    },
    {
      title: t('groupPage.createGroup.lastDeparture'),
      dataIndex: 'last_departure',
      key: 'last_departure',
      width: '150px',
      align: 'center',
      render: (value: string) => toLocalTime(value, 'DD/MM/YYYY')
    },
    {
      title: t('common.note'),
      dataIndex: 'note',
      key: 'note',
      width: '250px',
      render: renderTextWithEllipsis
    }
  ];

  return columnListGroup;
};

export const ACTION_SUBGROUP = {
  DETAIL: 'DETAIL',
  GENERATE_ROOM_LIST: 'GENERATE_ROOM_LIST',
  CANCEL_GROUP: 'CANCEL_GROUP'
};

export const getActionSubgroupOptions = (t: (key: string) => string): MenuProps['items'] => [
  {
    key: ACTION_SUBGROUP.DETAIL,
    label: t('groupPage.subGroup.detail')
  },
  {
    key: ACTION_SUBGROUP.GENERATE_ROOM_LIST,
    label: t('groupPage.subGroup.generateRoomList')
  },
  {
    key: ACTION_SUBGROUP.CANCEL_GROUP,
    label: t('groupPage.subGroup.cancel')
  }
];

export const getColumnDetailGroup = (t: (key: string) => string, onClick: (menu: any) => void) => {
  const ACTION_SUBGROUP_OPTIONS = getActionSubgroupOptions(t);
  const columnDetailGroup: TableProps<SubGroup>['columns'] = [
    {
      title: t('common.no'),
      dataIndex: 'stt',
      key: 'stt',
      width: '60px',
      align: 'center',
      render: (value, _, index: number) => index + 1
    },
    {
      title: t('bookingPage.roomMap.type'),
      dataIndex: 'room_type_name',
      key: 'room_type_name',
      width: '80px',
      align: 'center'
    },
    {
      title: t('groupPage.subGroup.roomQty'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: '80px',
      align: 'center'
    },
    {
      title: t('groupPage.subGroup.roomed'),
      dataIndex: 'roomed',
      key: 'roomed',
      width: '80px',
      align: 'center'
    },
    {
      title: t('groupPage.subGroup.pax'),
      dataIndex: 'pax',
      key: 'pax',
      width: '80px',
      align: 'center'
    },
    {
      title: t('groupPage.subGroup.roomedPax'),
      dataIndex: 'roomed_pax',
      key: 'roomed_pax',
      width: '80px',
      align: 'center'
    },
    {
      title: t('common.time.fromDate'),
      dataIndex: 'check_in',
      key: 'check_in',
      width: '180px',
      align: 'center',
      render: (value: string) => (value ? toLocalTime(value, 'DD/MM/YYYY') : '')
    },
    {
      title: t('common.time.toDate'),
      dataIndex: 'check_out',
      key: 'check_out',
      width: '180px',
      align: 'center',
      render: (value: string) => (value ? toLocalTime(value, 'DD/MM/YYYY') : '')
    },
    {
      title: t('groupPage.subGroup.rate'),
      dataIndex: 'rate_amount',
      key: 'rate_amount',
      width: '180px',
      align: 'center',
      render: formatNumber
    },
    {
      title: t('groupPage.subGroup.rmTotal'),
      dataIndex: 'rmTotal',
      key: 'rmTotal',
      width: '180px',
      align: 'center',
      render: (value, data) => {
        if (value || value === 0) return formatNumber(value);

        const nights = dayjs(data.check_out).diff(dayjs(data.check_in), 'day');
        return formatNumber(data.rate_amount * nights * data.quantity);
      }
    }
    // {
    //   title: 'SP Total',
    //   dataIndex: 'spTotal',
    //   key: 'spTotal',
    //   width: '150px',
    //   align: 'center',
    //   render: formatNumber
    // }
  ];

  columnDetailGroup.forEach(col => {
    const existingRender = col.render;

    col.render = (text, record, index) => {
      const actions = (ACTION_SUBGROUP_OPTIONS ?? []).filter(item => {
        if (
          record.dummy_booking &&
          (item?.key === ACTION_SUBGROUP.DETAIL || item?.key === ACTION_SUBGROUP.GENERATE_ROOM_LIST)
        ) {
          return false;
        }

        if (record.roomed >= record.quantity && item?.key === ACTION_SUBGROUP.GENERATE_ROOM_LIST) {
          return false;
        }

        return true;
      });

      return (
        <Dropdown
          trigger={['click']}
          menu={{ items: actions, onClick }}
          placement="bottom"
          overlayClassName="action-subgroup"
          destroyPopupOnHide
        >
          <div>{existingRender ? existingRender(text, record, index) : text}</div>
        </Dropdown>
      );
    };
  });

  return columnDetailGroup;
};

export const getColumnChangeDateGroup = (t: (key: string) => string) => {
  const MappingColumnBookingStatus = getMappingColumnBookingStatus(t);
  const columnChangeDateGroup: TableProps<SubGroup>['columns'] = [
    {
      title: t('changeDateGroup.confirm'),
      dataIndex: 'groupId',
      key: 'groupId',
      width: '80px',
      align: 'center'
    },
    {
      title: t('changeDateGroup.groupSubcode'),
      dataIndex: 'id',
      key: 'id',
      width: '100px',
      align: 'center'
    },
    {
      title: t('changeDateGroup.status'),
      dataIndex: 'status',
      key: 'status',
      width: '100px',
      align: 'center',
      render: text => MappingColumnBookingStatus[text] || ''
    },
    {
      title: t('changeDateGroup.roomType'),
      dataIndex: 'room_type_name',
      key: 'room_type_name',
      width: '80px',
      align: 'center'
    },
    {
      title: t('common.time.fromDate'),
      dataIndex: 'check_in',
      key: 'check_in',
      width: '180px',
      align: 'center',
      render: (value: string) => toLocalTime(value, 'DD/MM/YYYY')
    },
    {
      title: t('common.time.toDate'),
      dataIndex: 'check_out',
      key: 'check_out',
      width: '180px',
      align: 'center',
      render: (value: string) => toLocalTime(value, 'DD/MM/YYYY')
    }
  ];

  return columnChangeDateGroup;
};

export const getColumnUsedRoomsTab = (t: (key: string) => string) => {
  const MappingColumnBookingStatus = getMappingColumnBookingStatus(t);
  const columnsUsedRoomsTab: TableProps<any>['columns'] = [
    {
      title: t('common.bookingData.status'),
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: '60px',
      render: text => MappingColumnBookingStatus[text] || ''
    },
    {
      title: t('common.bookingData.bookingId'),
      dataIndex: 'booking_id',
      key: 'booking_id',
      align: 'center',
      width: '75px'
    },
    {
      title: t('common.bookingData.bookingLineId'),
      dataIndex: 'booking_line_id',
      key: 'booking_line_id',
      align: 'center',
      width: '65px'
    },
    {
      title: t('common.customer.name'),
      dataIndex: 'partner_name',
      key: 'partner_name',
      align: 'center',
      width: '140px'
    },
    {
      title: t('common.bookingData.room'),
      dataIndex: 'room_name',
      key: 'room_name',
      align: 'center',
      width: '50px',
      render: (_, record: BookingLine) => (
        <span className="room-id">
          {record.original_room_type_name !== record.room_type_name && (
            <span className="original-room-type" style={{ color: 'rgba(0, 0, 0, 0.2509803922)' }}>
              {record.original_room_type_name} &gt;
            </span>
          )}{' '}
          {record.room_name || record.room_type_name + ' N/A'}
        </span>
      )
    },
    {
      title: t('common.time.checkIn'),
      dataIndex: 'check_in',
      key: 'check_in',
      align: 'center',
      width: '90px',
      render: (value: string) => toLocalTime(value, 'DD/MM/YYYY')
    },
    {
      title: t('common.time.checkOut'),
      dataIndex: 'check_out',
      key: 'check_out',
      align: 'center',
      width: '90px',
      render: (value: string) => toLocalTime(value, 'DD/MM/YYYY')
    },
    {
      title: t('common.time.createdAt'),
      dataIndex: 'create_date',
      key: 'create_date',
      align: 'center',
      width: '90px',
      render: (date: string) => toLocalTime(date, 'DD/MM/YYYY HH:mm')
    }
  ];
  return columnsUsedRoomsTab;
};

export const getColumnSubGroupsTab = (t: (key: string) => string) => {
  const columnSubGroupsTab: TableProps<any>['columns'] = [
    {
      title: t('managePage.roomAvailabilityPage.groupNotCreateYet.refCode'),
      dataIndex: 'ref_code',
      key: 'ref_code',
      align: 'center',
      width: '80px'
    },
    {
      title: t('groupPage.groupList.groupCode'),
      dataIndex: 'code',
      key: 'code',
      align: 'center',
      width: '80px'
    },
    {
      title: t('groupPage.groupList.groupName'),
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      width: '100px'
    },
    {
      title: t('groupPage.createGroup.taCompany'),
      dataIndex: 'hotel_travel_agency_name',
      key: 'hotel_travel_agency_name',
      align: 'center',
      width: '140px'
    },
    {
      title: t('common.bookingData.roomType'),
      dataIndex: 'room_type_name',
      key: 'room_type_name',
      align: 'center',
      width: '80px'
    },
    {
      title: t('common.bookingData.roomQty'),
      dataIndex: 'remain_room',
      key: 'remain_room',
      align: 'center',
      width: '60px'
    },
    {
      title: t('common.bookingData.customerQty'),
      dataIndex: 'pax',
      key: 'pax',
      align: 'center',
      width: '60px'
    },
    {
      title: t('common.time.checkIn'),
      dataIndex: 'check_in',
      key: 'check_in',
      align: 'center',
      width: '60px'
    },
    {
      title: t('common.time.checkOut'),
      dataIndex: 'check_out',
      key: 'check_out',
      align: 'center',
      width: '60px'
    },
    {
      title: t('common.time.createdAt'),
      dataIndex: 'create_date',
      key: 'create_date',
      align: 'center',
      width: '100px'
    }
  ];
  return columnSubGroupsTab;
};

export const getColumnRoomsFocTab = (t: (key: string) => string) => {
  const columnRoomsFocTab: TableProps<any>['columns'] = [
    {
      title: t('common.bookingData.bookingId'),
      dataIndex: 'booking_id',
      key: 'booking_id',
      align: 'center',
      width: '40px',
      render: (text: string) => (text === t('common.totalCap') ? <strong>{text}</strong> : text)
    },
    {
      title: t('common.bookingData.reasonOrApprove'),
      dataIndex: 'partner_name',
      key: 'partner_name',
      width: '120px'
    },
    {
      title: t('common.bookingData.roomType'),
      dataIndex: 'room_type',
      key: 'room_type',
      width: '50px'
    },
    {
      title: t('managePage.focsReportPage.focRoom.sourceName'),
      dataIndex: 'source_name',
      align: 'center',
      key: 'source_name',
      width: '60px'
    },
    {
      title: t('managePage.focsReportPage.focRoom.roomQty'),
      dataIndex: 'room_qty',
      key: 'room_qty',
      align: 'center',
      width: '60px'
    },
    {
      title: t('managePage.focsReportPage.focRoom.level'),
      dataIndex: 'guest_level',
      key: 'guest_level',
      width: '60px',
      render: value => {
        const genderKey = MAP_GUEST_LEVEL[value];
        return GUEST_LEVEL_NAME[genderKey as keyof typeof GUEST_LEVEL_NAME];
      }
    },
    {
      title: t('managePage.focsReportPage.focRoom.roomPrice'),
      dataIndex: 'room_price',
      key: 'room_price',
      align: 'center',
      width: '60px',
      render: value => formatInputMoney(value)
    },
    {
      title: t('common.actions.checkIn'),
      dataIndex: 'check_in',
      key: 'check_in',
      align: 'center',
      width: '80px',
      render: value => (value ? toLocalTime(value, 'DD/MM/YYYY HH:mm:ss') : '')
    },
    {
      title: t('common.actions.checkOut'),
      dataIndex: 'check_out',
      key: 'check_out',
      align: 'center',
      width: '80px',
      render: value => (value ? toLocalTime(value, 'DD/MM/YYYY HH:mm:ss') : '')
    },
    {
      title: t('managePage.focsReportPage.common.dateOfFoc'),
      dataIndex: 'date_of_foc',
      key: 'date_of_foc',
      align: 'center',
      width: '60px',
      render: (value: string) => value && toLocalTime(value, 'DD/MM/YYYY')
    }
  ];
  return columnRoomsFocTab;
};

export const getColumnServiceFocTab = (t: (key: string) => string) => {
  const columnServiceFocTab: TableProps<any>['columns'] = [
    {
      title: t('common.bookingData.bookingId'),
      dataIndex: 'booking_id',
      key: 'booking_id',
      width: '60px',
      render: (text: string) => (text === 'TOTAL' ? <strong>{text}</strong> : text)
    },
    {
      title: t('common.customer.name'),
      dataIndex: 'partner_name',
      key: 'partner_name',
      width: '120px'
    },
    {
      title: t('common.bookingData.services.service'),
      dataIndex: 'service_name',
      key: 'service_name',
      width: '80px'
    },
    {
      title: t('common.bookingData.defaultPrice'),
      dataIndex: 'service_default_price',
      key: 'service_default_price',
      width: '50px',
      render: value => (value ? formatCurrency(value) : '')
    },
    {
      title: t('common.bookingData.services.qty'),
      dataIndex: 'services_qty',
      key: 'services_qty',
      width: '50px'
    },
    {
      title: t('managePage.focsReportPage.focService.calculatePrice'),
      dataIndex: 'calculate_price',
      key: 'calculate_price',
      align: 'center',
      width: '50px',
      render: (_, { calculate_price }) => formatCurrency(calculate_price)
    },
    {
      title: t('managePage.focsReportPage.common.dateOfFoc'),
      dataIndex: 'date_of_foc',
      key: 'date_of_foc',
      width: '60px',
      render: (value: string) => value && toLocalTime(value, 'DD/MM/YYYY')
    }
  ];
  return columnServiceFocTab;
};

export const getColumnCustomersManagement = (t: (key: string) => string) => {
  const columnCustomersManagement: TableProps<PersonalInfoType>['columns'] = [
    {
      title: t('common.customer.customerId'),
      dataIndex: 'partner_id',
      key: 'partner_id',
      width: '50px',
      align: 'left'
    },
    {
      title: t('common.customer.fullName'),
      dataIndex: 'name',
      key: 'name',
      width: '100px',
      align: 'left'
    },
    {
      title: t('common.note'),
      key: 'comment',
      dataIndex: 'comment',
      width: '100px',
      align: 'left',
      render: renderTextWithEllipsis
    },
    {
      title: t('common.customer.gender'),
      key: 'gender',
      dataIndex: 'gender',
      width: '40px',
      align: 'left',
      render: value => {
        const genderCode = MAP_GENDER[value]; // e.g. 'F'
        const genderKey = GENDER_NAME[genderCode as keyof typeof GENDER_NAME]; // e.g. 'common.customer.female'
        return t(genderKey); // e.g. t('common.customer.female') => "Female"
      }
    },
    {
      title: t('common.customer.dob'),
      dataIndex: 'dob',
      key: 'dob',
      width: '70px',
      align: 'left',
      render: (value: string) => value && dayjs(value).format('DD/MM/YYYY')
    },
    {
      title: t('common.customer.identificationCard'),
      dataIndex: 'identification',
      key: 'identification',
      width: '70px',
      align: 'left'
    },
    {
      title: t('common.customer.taxCode'),
      key: 'vat',
      dataIndex: 'vat',
      width: '70px',
      align: 'left'
    },
    {
      title: t('common.customer.phone'),
      dataIndex: 'phone',
      key: 'phone',
      width: '70px',
      align: 'left'
    },
    {
      title: t('common.customer.email'),
      key: 'email',
      dataIndex: 'email',
      width: '120px',
      align: 'left'
    },
    {
      title: t('common.customer.nationality'),
      key: 'country',
      dataIndex: 'country',
      width: '50px',
      align: 'left',
      render: (_, record: PersonalInfoType) =>
        record.country && renderTextWithEllipsis(record.country.name)
    }
  ];
  return columnCustomersManagement;
};

export const getColumnCustomerBookings = (t: (key: string) => string) => {
  const MappingColumnBookingStatus = getMappingColumnBookingStatus(t);
  const columnCustomerBookings: TableProps<BookingLine>['columns'] = [
    {
      title: 'ID',
      dataIndex: 'booking_line_id',
      key: 'booking_line_id',
      width: '60px',
      align: 'center'
    },
    {
      title: t('common.bookingData.bookingId'),
      dataIndex: 'booking_id',
      key: 'booking_id',
      width: '100px',
      align: 'center'
    },
    {
      title: t('common.bookingData.branch'),
      dataIndex: 'branch',
      key: 'branch',
      width: '120px',
      align: 'center',
      render: (_, record: BookingLine) => record.branch.name
    },
    {
      title: t('common.bookingData.status'),
      dataIndex: 'status',
      key: 'status',
      width: '100px',
      align: 'center',
      render: text => MappingColumnBookingStatus[text] || ''
    },
    {
      title: t('common.bookingData.room'),
      dataIndex: 'room_name',
      key: 'room_name',
      width: '100px',
      align: 'center',
      render: (_, record: BookingLine) => (
        <span className="room-id">
          {record.original_room_type_name !== record.room_type_name && (
            <span className="original-room-type" style={{ color: 'rgba(0, 0, 0, 0.2509803922)' }}>
              {record.original_room_type_name} &gt;
            </span>
          )}{' '}
          {record.room_name || record.room_type_name + ' N/A'}
        </span>
      )
    },
    {
      title: t('common.customer.name'),
      dataIndex: 'partner_name',
      key: 'partner_name',
      width: '220px',
      align: 'center'
    },
    {
      title: t('bookingPage.bookingList.adultAndChild'),
      width: '60px',
      align: 'center',
      render: (_, record) => `${record.adult}/${record.child}`
    },
    {
      title: t('common.note'),
      dataIndex: 'note',
      key: 'note',
      width: '250px',
      align: 'center',
      render: renderTextWithEllipsis
    },
    {
      title: t('bookingPage.bookingList.labels'),
      dataIndex: 'labels',
      key: 'labels',
      width: '160px',
      render: (_, record) => (
        <span className="labels">
          {record.labels.map(label => (
            <span
              key={label.name}
              style={{
                backgroundColor: label.color,
                color: '#fff',
                padding: '2px 5px',
                margin: '0 2px',
                borderRadius: '3px'
              }}
            >
              {label.name}
            </span>
          ))}
        </span>
      )
    },
    {
      title: t('common.time.checkIn'),
      key: 'check_in',
      dataIndex: 'check_in',
      width: '120px',
      align: 'center',
      render: value => toLocalTime(value, 'DD-MM-YYYY')
    },
    {
      title: t('common.time.checkOut'),
      dataIndex: 'check_out',
      key: 'check_out',
      width: '120px',
      align: 'center',
      render: value => toLocalTime(value, 'DD-MM-YYYY')
    },
    {
      title: t('common.bookingData.priceList'),
      dataIndex: 'pricelist',
      key: 't',
      width: '120px',
      align: 'center',
      render: value => value?.name || t('common.bookingData.defaultPrice')
    }
  ];
  return columnCustomerBookings;
};

export const getColumnsConsolidateRevenue = (
  title: string,
  t: (key: string) => string,
  onShowOcc?: boolean,
  sortOptions?: {
    isSourceTab?: boolean;
    isSorted?: boolean;
    onSortToggle?: () => void;
  }
) => {
  const columnsConsolidateRevenue: TableProps<any>['columns'] = [
    {
      title: sortOptions?.isSourceTab ? (
        <div
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            userSelect: 'none'
          }}
          onClick={sortOptions?.onSortToggle}
        >
          <span>{title}</span>
          {sortOptions?.isSorted ? <UpOutlined /> : <DownOutlined />}
        </div>
      ) : (
        `${title}`
      ),
      dataIndex: 'roomType',
      key: 'roomType',
      width: '120px',
      align: 'left'
    },
    {
      title: t('common.bookingData.roomPrice'),
      dataIndex: 'room_price',
      key: 'room_price',
      width: '150px',
      align: 'right',
      render: value => (value ? value.toLocaleString() : '0')
    },
    {
      title: t('common.bookingData.services.servicePrice'),
      dataIndex: 'service_cost',
      key: 'service_cost',
      width: '150px',
      align: 'right',
      render: value => (value ? value.toLocaleString() : '0')
    },
    {
      title: t('common.bookingData.discount'),
      dataIndex: 'discount',
      key: 'discount',
      width: '100px',
      align: 'right',
      render: value => (value ? value.toLocaleString() : '0')
    },
    {
      title: t('common.bookingData.revenue'),
      dataIndex: 'revenue',
      key: 'revenue',
      width: '150px',
      align: 'right',
      render: value => (value ? value.toLocaleString() : '0')
    },
    {
      title: t('common.bookingData.totalNight'),
      dataIndex: 'total_night',
      key: 'total_night',
      width: '100px',
      align: 'right',
      render: value => value || 0
    },
    {
      title: (
        <span>
          {t('common.bookingData.occPercentTotal')}{' '}
          <Tooltip
            title={
              <div style={{ textAlign: 'center' }}>
                <span
                  style={{
                    display: 'block',
                    borderBottom: '2px solid white',
                    paddingBottom: '4px'
                  }}
                >
                  {t('managePage.consolidateRevenueReportPage.toolTip.totalNightsSold')}
                </span>
                <span style={{ display: 'block' }}>
                  {t('managePage.consolidateRevenueReportPage.toolTip.totalRoomInventory')}
                </span>
              </div>
            }
          >
            <InfoCircleOutlined style={{ cursor: 'pointer' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'occupied_percent_total',
      key: 'occupied_percent_total',
      width: '200px',
      align: 'right',
      render: value => {
        return value !== '' ? `${Number(value)}%` : '0%';
      }
    },
    {
      title: (
        <span>
          {t('common.bookingData.averagePrice')}{' '}
          <Tooltip
            title={
              <div style={{ textAlign: 'center' }}>
                <span
                  style={{
                    display: 'block',
                    borderBottom: '2px solid white',
                    paddingBottom: '4px'
                  }}
                >
                  {t('common.bookingData.roomPrice')}
                </span>
                <span style={{ display: 'block' }}>
                  {t('managePage.consolidateRevenueReportPage.toolTip.numberOfRoomSold')}
                </span>
              </div>
            }
          >
            <InfoCircleOutlined style={{ cursor: 'pointer' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'average_price',
      key: 'average_price',
      width: '150px',
      align: 'right',
      render: value => (value ? value.toLocaleString() : '0')
    }
  ];

  if (onShowOcc) {
    columnsConsolidateRevenue.splice(6, 0, {
      title: (
        <span>
          {t('common.bookingData.occPercent')}{' '}
          <Tooltip
            title={
              <div style={{ textAlign: 'center' }}>
                <span
                  style={{
                    display: 'block',
                    borderBottom: '2px solid white',
                    paddingBottom: '4px'
                  }}
                >
                  {t('managePage.consolidateRevenueReportPage.toolTip.totalNightsSold')}
                </span>
                <span style={{ display: 'block' }}>
                  {t('managePage.consolidateRevenueReportPage.toolTip.totalAvailableRoomsType')}
                </span>
              </div>
            }
          >
            <InfoCircleOutlined style={{ cursor: 'pointer' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'occupied_percent',
      key: 'occupied_percent',
      width: '150px',
      align: 'right',
      render: (value: any) => (value !== '' ? `${Number(value)}%` : '0%')
    });
  }
  return columnsConsolidateRevenue;
};

export const getColumnConsolidateSourceRevenue = (t: (key: string) => string) => {
  const columnsConsolidateSourceRevenue: TableProps<any>['columns'] = [
    {
      dataIndex: 'hidden',
      key: 'hidden',
      width: '32px'
    },
    {
      title: t(`managePage.consolidateRevenueReportPage.common.titleSource`),
      dataIndex: 'ta_name',
      key: 'ta_name',
      width: '120px',
      align: 'left'
    },
    {
      title: t('common.bookingData.roomPrice'),
      dataIndex: 'room_price',
      key: 'room_price',
      width: '150px',
      align: 'right',
      render: value => (value ? Math.round(value).toLocaleString() : '0')
    },
    {
      title: t('common.bookingData.services.servicePrice'),
      dataIndex: 'service_cost',
      key: 'service_cost',
      width: '150px',
      align: 'right',
      render: value => (value ? value.toLocaleString() : '0')
    },
    {
      title: t('common.bookingData.discount'),
      dataIndex: 'discount',
      key: 'discount',
      width: '100px',
      align: 'right',
      render: value => (value ? value.toLocaleString() : '0')
    },
    {
      title: t('common.bookingData.revenue'),
      dataIndex: 'revenue',
      key: 'revenue',
      width: '150px',
      align: 'right',
      render: value => (value ? value.toLocaleString() : '0')
    },
    {
      title: t('common.bookingData.totalNight'),
      dataIndex: 'total_night',
      key: 'total_night',
      width: '100px',
      align: 'right',
      render: value => value || 0
    },
    {
      title: t('common.bookingData.occPercentTotal'),
      dataIndex: 'occupied_percent_total',
      key: 'occupied_percent_total',
      width: '200px',
      align: 'right',
      render: value => {
        return value != '' ? `${Number(value)}%` : '0%';
      }
    },
    {
      title: t('common.bookingData.averagePrice'),
      dataIndex: 'average_price',
      key: 'average_price',
      width: '150px',
      align: 'right',
      render: value => (value ? value.toLocaleString() : '0')
    }
  ];

  return columnsConsolidateSourceRevenue;
};

export const getColumnDetailedAnalysisReport = (t: (key: string) => string) => {
  const columnDetailedAnalysisReport: TableProps<DetailedAnalysisReport>['columns'] = [
    {
      title: t('common.bookingData.saleOrderRef'),
      dataIndex: 'sale_order_ref',
      key: 'sale_order_ref',
      width: '120px',
      align: 'center'
    },
    {
      title: t('common.bookingData.guestName'),
      dataIndex: 'guest_name',
      key: 'guest_name',
      width: '120px',
      align: 'center'
    },
    {
      title: t('common.bookingData.roomType'),
      dataIndex: 'room_type_name',
      key: 'room_type_name',
      width: '100px',
      align: 'center'
    },
    {
      title: t('bookingPage.roomLock.roomNo'),
      dataIndex: 'room_no',
      key: 'room_no',
      width: '100px',
      align: 'center'
    },
    {
      title: t('common.time.checkIn'),
      dataIndex: 'check_in',
      key: 'check_in',
      width: '150px',
      align: 'center',
      render: value => (value ? toLocalTime(value, 'DD-MM-YYYY HH:mm:ss') : '')
    },
    {
      title: t('common.time.checkOut'),
      dataIndex: 'check_out',
      key: 'check_out',
      width: '150px',
      align: 'center',
      render: value => (value ? toLocalTime(value, 'DD-MM-YYYY HH:mm:ss') : '')
    },
    {
      title: t('bookingPage.bookingList.source'),
      dataIndex: 'source',
      key: 'source',
      width: '120px',
      align: 'center'
    },
    {
      title: t('common.bookingData.totalNight'),
      dataIndex: 'booking_days',
      key: 'booking_days',
      width: '120px',
      align: 'center'
    },
    {
      title: t('common.bookingData.totalRoomPrice'),
      dataIndex: 'room_price',
      key: 'room_price',
      width: '120px',
      align: 'center',
      render: value => formatInputNumber(Math.round(value))
    },
    {
      title: t('common.bookingData.services.service'),
      dataIndex: 'service_price',
      key: 'service_price',
      width: '120px',
      align: 'center',
      render: value => formatInputNumber(Math.round(value))
    },
    {
      title: t('managePage.detailedAnalysisReport.discountTotal'),
      dataIndex: 'discount_total',
      key: 'discount_total',
      width: '130px',
      align: 'center',
      render: value => formatInputNumber(Math.round(value))
    },
    {
      title: t('common.bookingData.revenue'),
      dataIndex: 'total_revenue',
      key: 'total_revenue',
      width: '140px',
      align: 'center',
      render: value => formatInputNumber(Math.round(value))
    }
  ];
  return columnDetailedAnalysisReport;
};

export const getColumnFolioRevenueReport = (t: (key: string) => string) => {
  const columnFolioRevenueReport: TableProps<FolioRevenueReport>['columns'] = [
    {
      title: t('common.bookingData.saleOrderRef'),
      dataIndex: 'sale_order_ref',
      key: 'sale_order_ref',
      width: '120px',
      align: 'center'
    },
    {
      title: t('common.bookingData.guestName'),
      dataIndex: 'guest_name',
      key: 'guest_name',
      width: '120px',
      align: 'center'
    },
    {
      title: t('common.bookingData.roomType'),
      dataIndex: 'room_type_name',
      key: 'room_type_name',
      width: '100px',
      align: 'center'
    },
    {
      title: t('bookingPage.roomLock.roomNo'),
      dataIndex: 'room_no',
      key: 'room_no',
      width: '100px',
      align: 'center'
    },
    {
      title: t('common.time.checkIn'),
      dataIndex: 'check_in',
      key: 'check_in',
      width: '150px',
      align: 'center',
      render: value => (value ? toLocalTime(value, 'DD-MM-YYYY HH:mm:ss') : '')
    },
    {
      title: t('common.time.checkOut'),
      dataIndex: 'check_out',
      key: 'check_out',
      width: '150px',
      align: 'center',
      render: value => (value ? toLocalTime(value, 'DD-MM-YYYY HH:mm:ss') : '')
    },
    {
      title: t('bookingPage.bookingList.source'),
      dataIndex: 'source',
      key: 'source',
      width: '120px',
      align: 'center'
    },
    {
      title: t('managePage.folioRevenueReport.invoiceDate'),
      dataIndex: 'invoice_date',
      key: 'invoice_date',
      width: '120px',
      align: 'center',
      render: value => (value ? toLocalTime(value, 'DD-MM-YYYY') : '')
    },
    {
      title: t('managePage.folioRevenueReport.taxExcluded'),
      dataIndex: 'tax_excluded',
      key: 'tax_excluded',
      width: '140px',
      align: 'center',
      render: value => formatInputNumber(Math.round(value))
    },
    {
      title: t('managePage.folioRevenueReport.total'),
      dataIndex: 'total',
      key: 'total',
      width: '140px',
      align: 'center',
      render: value => formatInputNumber(Math.round(value))
    }
  ];
  return columnFolioRevenueReport;
};

export const getColumnToursData = (
  t: (key: string) => string,
  onClick: (menu: any, record: any) => void
) => {
  const columnToursData: TableProps<TourData>['columns'] = [
    {
      title: t('tour.tourName'),
      dataIndex: 'tourName',
      key: 'tourName',
      width: '40%'
    },
    {
      title: t('tour.status'),
      dataIndex: 'tourStatus',
      key: 'tourName',
      width: '20%',
      render: (tourStatus: string) => {
        return (TOUR_STATUS_NAMES as any)[tourStatus] || tourStatus;
      }
    },
    {
      title: t('tour.tourType'),
      dataIndex: 'isSingleTour',
      key: 'isSingleTour',
      width: '20%',
      render: (isSingleTour: boolean) => (
        <Tag color={isSingleTour ? 'blue' : 'green'}>
          {isSingleTour ? t('tour.singleTour') : t('tour.tourPackage')}
        </Tag>
      )
    },
    {
      title: t('tour.totalCustomers'),
      dataIndex: 'totalCustomers',
      key: 'totalCustomers',
      width: '20%'
    }
  ];

  // Add action menu to each column
  columnToursData.forEach(col => {
    const existingRender = col.render;
    col.render = (text, record, index) => {
      const menu = ACTION_TOUR_LIST(t, record);

      return (
        <Dropdown
          trigger={['click']}
          menu={{
            items: menu,
            onClick: menuItem => onClick(menuItem, record)
          }}
          placement="bottom"
          overlayClassName="action-booking-list"
          destroyPopupOnHide
        >
          <div>{existingRender ? existingRender(text, record, index) : text}</div>
        </Dropdown>
      );
    };
  });

  return columnToursData;
};

export const getExpandableToursData = (t: (key: string) => string) => {
  // Expanded content columns
  const expandedColumns: TableProps<BookingData>['columns'] = [
    {
      title: t('tour.bookingId'),
      dataIndex: 'bookingId',
      key: 'bookingId',
      width: '5%'
    },
    {
      title: t('tour.branchName'),
      dataIndex: 'branchName',
      key: 'branchName',
      width: '15%'
    },
    {
      title: t('tour.customerName'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: '15%'
    },
    {
      title: t('tour.travelDate'),
      dataIndex: 'travelDate',
      key: 'travelDate',
      width: '5%',
      render: value => toFormatDate(value)
    },
    {
      title: t('tour.guest'),
      dataIndex: 'guestType',
      key: 'guestType',
      width: '5%',
      render: (guestType: string) => (
        <Tag color={guestType === 'adult' ? 'blue' : 'green'}>
          {guestType === 'adult'
            ? t('agGrid.adult')
            : guestType === 'child'
              ? t('agGrid.child')
              : ''}
        </Tag>
      )
    },
    {
      title: t('tour.customerPhone'),
      dataIndex: 'customerPhone',
      key: 'customerPhone',
      width: '10%'
    },
    {
      title: t('tour.customerEmail'),
      dataIndex: 'customerEmail',
      key: 'customerEmail',
      width: '15%'
    },
    {
      title: t('tour.note'),
      dataIndex: 'note',
      key: 'note',
      width: '15%'
    }
  ];
  return expandedColumns;
};

export const getColumnEInvoiceStatus = (t: (key: string) => string) => {
  const columnEInvoiceStatus: TableProps<any>['columns'] = [
    {
      title: t('eInvoiceStatus.bookingId'),
      dataIndex: 'booking_id',
      key: 'booking_id',
      align: 'center',
      width: '60px',
      render: (value: string, row: any) => renderWithRowSpan(value, row, 'rowSpan')
    },
    {
      title: t('eInvoiceStatus.guestName'),
      dataIndex: 'guest_name',
      key: 'guest_name',
      align: 'center',
      width: '100px',
      render: (value: string, row: any) => renderWithRowSpan(value, row, 'rowSpan')
    },
    {
      title: t('eInvoiceStatus.travelAgencyName'),
      dataIndex: 'travel_agency_name',
      key: 'travel_agency_name',
      align: 'center',
      width: '100px',
      render: (value: string, row: any) => renderWithRowSpan(value, row, 'rowSpan')
    },
    {
      title: t('eInvoiceStatus.checkInDate'),
      dataIndex: 'check_in_date',
      key: 'check_in_date',
      align: 'center',
      width: '80px',
      render: (value: string, row: any) => renderWithRowSpan(toFormatDate(value), row, 'rowSpan')
    },
    {
      title: t('eInvoiceStatus.checkOutDate'),
      dataIndex: 'check_out_date',
      key: 'check_out_date',
      align: 'center',
      width: '80px',
      render: (value: string, row: any) => renderWithRowSpan(toFormatDate(value), row, 'rowSpan')
    },
    {
      title: t('eInvoiceStatus.saleOrderName'),
      dataIndex: 'sale_order_name',
      key: 'sale_order_name',
      align: 'center',
      width: '50px',
      render: (value: string, record: any) => {
        if (record.isTotalRow) {
          return <strong>{value}</strong>;
        }
        return value;
      }
    },
    {
      title: t('eInvoiceStatus.unrequested'),
      dataIndex: 'unrequested',
      key: 'unrequested',
      align: 'center',
      width: '80px',
      render: (value: boolean) => (
        <span style={{ color: value ? 'green' : '' }}>{value ? '✓' : ''}</span>
      )
    },
    {
      title: t('eInvoiceStatus.requested'),
      dataIndex: 'requested',
      key: 'requested',
      align: 'center',
      width: '80px',
      render: (value: boolean) => (
        <span style={{ color: value ? 'green' : '' }}>{value ? '✓' : ''}</span>
      )
    },
    {
      title: t('eInvoiceStatus.status'),
      dataIndex: 'minvoice_signed',
      key: 'minvoice_signed',
      align: 'center',
      width: '100px',
      render: (value: boolean) => (
        <span style={{ color: value ? 'green' : 'inherit', fontWeight: value ? 'bold' : 'normal' }}>
          {value ? 'DONE' : 'NOT YET'}
        </span>
      )
    }
  ];
  return columnEInvoiceStatus;
};

export const getColumnSplitTransactions = (
  currentQuantity: number,
  handleDateChange: (date: any, recordKey: string) => void,
  removeTransactionLine?: (recordKey: string) => void,
  splitMethod?: string,
  handleSplitPercentChange?: (value: number, recordKey: string) => void,
  handleSplitAmountChange?: (value: number, recordKey: string) => void,
  handleSplitQuantityChange?: (value: number, recordKey: string) => void
) => {
  const columnSplitTransactions: TableProps<any>['columns'] = [
    {
      title: 'Transaction Name',
      dataIndex: 'transaction_name',
      key: 'transaction_name',
      width: 200
    },
    {
      title: 'Date',
      dataIndex: 'transaction_date',
      key: 'transaction_date',
      width: 120,
      render: (date: string, record: any) => (
        <DatePicker
          value={date ? dayjs(date) : null}
          onChange={date => handleDateChange(date, record.key)}
          format="DD/MM/YYYY"
          style={{ width: '120px' }}
          allowClear={false}
          disabled
        />
      )
    },
    {
      title: 'Unit Amount',
      dataIndex: 'transaction_amount',
      key: 'transaction_amount',
      width: 120,
      render: (amount: number) => formatInputNumber(Math.round(amount))
    },
    {
      title: 'Total Quantity',
      dataIndex: 'transaction_qty',
      key: 'transaction_qty',
      width: 100,
      render: (_, record: any) => {
        if (record.key === record.transaction_id?.toString()) {
          return currentQuantity;
        } else if (record.key.startsWith('new-')) {
          return formatInputNumber(
            Math.round(record.original_available_quantity || record.remain_quantity || 0)
          );
        }
        return formatInputNumber(Math.round(record.transaction_qty || 0));
      }
    },
    {
      title: 'Split Quantity',
      key: 'split_quantity',
      width: 120,
      render: (_, record: any) => (
        <InputNumber
          min={0}
          max={
            record.key.startsWith('new-')
              ? record.original_available_quantity || record.remain_quantity || 0
              : record.transaction_qty
          }
          value={record.split_quantity || 0}
          onChange={val => handleSplitQuantityChange?.(val || 0, record.key)}
          style={{ width: '80px' }}
          disabled={currentQuantity == 1}
        />
      )
    },
    ...(currentQuantity > 1
      ? [
          {
            title: 'Remain Quantity',
            key: 'remain_quantity',
            width: 120,
            render: (_: any, record: any) =>
              formatInputNumber(Math.round(record.remain_quantity || 0))
          }
        ]
      : []),
    {
      title: 'Total Amount',
      key: 'total_amount',
      width: 120,
      render: (_, record: any) => {
        // For first line, show the original total amount
        // For subsequent lines, show the available amount from previous line's remain amount
        if (currentQuantity > 1) {
          return formatInputNumber(Math.round(record.transaction_amount * record.transaction_qty));
        } else if (record.key === record.transaction_id?.toString()) {
          return formatInputNumber(Math.round(record.transaction_amount * record.transaction_qty));
        } else {
          // This is a new line, show the original available amount (don't change when split amount changes)
          return formatInputNumber(
            Math.round(record.original_available_amount || record.remain_amount || 0)
          );
        }
      }
    },
    ...(splitMethod === 'amount' && currentQuantity == 1
      ? [
          {
            title: 'Split Amount',
            key: 'split_amount',
            width: 120,
            render: (_: any, record: any) => {
              return (
                <InputNumber
                  min={0}
                  max={
                    record.key.startsWith('new-')
                      ? record.original_available_amount || record.remain_amount || 0
                      : record.transaction_amount * record.transaction_qty
                  }
                  value={record.split_amount || 0}
                  onChange={val => handleSplitAmountChange?.(val || 0, record.key)}
                  style={{ width: '100px' }}
                  formatter={value => formatInputNumber(value)}
                  disabled={currentQuantity > 1}
                />
              );
            }
          }
        ]
      : []),
    ...(splitMethod === 'percent' && currentQuantity == 1
      ? [
          {
            title: 'Split Percent',
            key: 'split_percent',
            width: 120,
            render: (_: any, record: any) => {
              return (
                <InputNumber
                  min={0}
                  max={100}
                  value={record.split_percent || 0}
                  onChange={val => handleSplitPercentChange?.(val || 0, record.key)}
                  style={{ width: '100px' }}
                  formatter={value => formatInputNumber(value)}
                  disabled={currentQuantity > 1}
                />
              );
            }
          }
        ]
      : []),
    ...(currentQuantity == 1
      ? [
          {
            title: 'Remain Amount',
            key: 'remain_amount',
            width: 120,
            render: (_: any, record: any) =>
              formatInputNumber(Math.round(record.remain_amount || 0))
          }
        ]
      : []),
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_, record: any) =>
        removeTransactionLine && record.key !== record.transaction_id?.toString() ? (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeTransactionLine(record.key)}
            style={{ padding: '4px 8px' }}
          />
        ) : null
    }
  ];
  return columnSplitTransactions;
};

export const getColumnBookingReminds = (
  t: (key: string) => string,
  onEdit?: (record: any) => void
): TableProps<any>['columns'] => {
  const columnBookingReminds: TableProps<any>['columns'] = [
    {
      title: t('bookingReminds.bookingId'),
      dataIndex: 'booking_id',
      key: 'booking_line_id',
      width: 80,
      align: 'left'
    },
    {
      title: t('bookingReminds.bookingLineId'),
      dataIndex: 'booking_line_id',
      key: 'booking_line_id',
      width: 80,
      align: 'left'
    },
    {
      title: t('common.categoties.category'),
      width: 60,
      align: 'left',
      render: (_: string, record: any) => {
        const isOther = record.category === CategoriesBookingReminderType.OTHER;
        const displayValue =
          record.category_display && isOther
            ? record.category_display
            : t(
                REMIND_CATEGORIES_OPTIONS.find(item => item.value === record.category)?.label || ''
              );

        const tag = (
          <Tag
            color={isOther ? 'warning' : 'success'}
            style={{
              backgroundColor: isOther ? '#FFF7E6' : '#E4FBE5',
              fontWeight: 'bold'
            }}
          >
            {displayValue}
          </Tag>
        );

        if (!displayValue) return null;

        return isOther ? <Tooltip title={t('common.categoties.other')}>{tag}</Tooltip> : tag;
      }
    },
    {
      title: t('common.bookingRemind.roomNo'),
      width: 60,
      align: 'left',
      render: (record: any) =>
        record?.room_no.room_no
          ? `${record.room_type_name} (${record.room_no.room_no})`
          : `${record.room_type_name} (N/A)`
    },
    {
      title: t('common.customer.mainGuest'),
      dataIndex: 'main_guest',
      key: 'main_guest',
      width: 160
    },
    {
      title: t('common.customer.gender'),
      dataIndex: 'gender',
      key: 'gender',
      width: 60,
      align: 'left',
      render: (gender: string) => {
        if (gender === 'male') return t('common.customer.male');
        if (gender === 'female') return t('common.customer.female');
        return '-';
      }
    },
    {
      title: 'TA',
      dataIndex: 'travel_agency',
      key: 'travel_agency',
      width: 120
    },
    {
      title: t('bookingReminds.remindContent'),
      dataIndex: 'note',
      key: 'note',
      width: 200,
      render: (note: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {renderTextWithEllipsis(note)}
          {onEdit && (
            <Button
              type="link"
              icon={<EditFilled />}
              onClick={() => onEdit(record)}
              style={{ padding: '4px' }}
            />
          )}
        </div>
      )
    },
    {
      title: t('bookingReminds.remindDate'),
      dataIndex: 'date',
      key: 'date',
      width: 80,
      align: 'left',
      render: (date: string) => (date ? toLocalTime(date, 'DD/MM/YYYY') : '-')
    },
    {
      title: t('bookingReminds.createdDate'),
      dataIndex: 'created_date',
      key: 'created_date',
      width: 80,
      align: 'left',
      render: (date: string) => (date ? toLocalTime(date, 'DD/MM/YYYY HH:mm') : '-')
    },
    {
      title: t('bookingReminds.creator'),
      dataIndex: 'user_name',
      key: 'user_name',
      width: 80
    },
    {
      title: t('common.time.checkIn'),
      dataIndex: 'check_in',
      key: 'check_in',
      width: 80,
      align: 'left',
      render: (date: string) => (date ? toLocalTime(date, 'DD/MM/YYYY HH:mm') : '-')
    },
    {
      title: t('common.time.checkOut'),
      dataIndex: 'check_out',
      key: 'check_out',
      width: 80,
      align: 'left',
      render: (date: string) => (date ? toLocalTime(date, 'DD/MM/YYYY HH:mm') : '-')
    }
  ];
  return columnBookingReminds;
};

export const getColumnServicesReport = (t: (key: string) => string) => {
  const columnServicesReport: TableProps<any>['columns'] = [
    {
      title: t('servicesReport.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center'
    },
    {
      title: t('servicesReport.branch'),
      dataIndex: 'branch_name',
      key: 'branch_name',
      width: '120px',
      align: 'center'
    },
    {
      title: t('servicesReport.saleOrderName'),
      dataIndex: 'sale_order_name',
      key: 'sale_order_name',
      width: '120px',
      align: 'center'
    },
    {
      title: t('servicesReport.bookingId'),
      dataIndex: 'booking_id',
      key: 'booking_id',
      width: 100,
      align: 'center'
    },
    {
      title: t('servicesReport.bookingLineId'),
      dataIndex: 'booking_line_id',
      key: 'booking_line_id',
      width: 120,
      align: 'center'
    },
    {
      title: t('servicesReport.productName'),
      dataIndex: 'product_name',
      key: 'product_name',
      width: 200,
      align: 'left'
    },
    {
      title: t('servicesReport.productId'),
      dataIndex: 'product_id',
      key: 'product_id',
      width: 100,
      align: 'center'
    },
    {
      title: t('servicesReport.totalPrice'),
      dataIndex: 'price',
      key: 'price',
      width: 120,
      align: 'right',
      render: (_, record: any) => `${formatCurrency(record.price * record.quantity || 0)}`
    },
    {
      title: t('servicesReport.discount'),
      dataIndex: 'discount',
      key: 'discount',
      width: 120,
      align: 'right',
      render: (value: number) => `${formatCurrency(value)}`
    },
    {
      title: t('servicesReport.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center',
      render: (value: number) => value
    },
    {
      title: t('servicesReport.transactionDate'),
      dataIndex: 'transaction_date',
      key: 'transaction_date',
      width: 120,
      align: 'center',
      render: (date: string) => (date ? toLocalTime(date, 'DD/MM/YYYY') : '-')
    }
  ];
  return columnServicesReport;
};

export const getViewKinSimDetailsColumns = (
  t: (key: string, options?: Record<string, unknown>) => string,
  onCopyUrl: (url: string) => void,
  onPreviewQr?: (url: string, title?: string) => void
): TableProps<ServiceKinSimUrlsType>['columns'] => {
  const { Text } = Typography;
  return [
    {
      title: t('esim.viewDetailsModal.qrCode', { defaultValue: 'QR Code' }),
      key: 'qr',
      width: 120,
      render: (_: unknown, record: ServiceKinSimUrlsType) => {
        const isAvailable = record.status?.toLowerCase() === 'available';
        if (isAvailable) {
          return (
            <div className="view-esim-details__qr-placeholder">
              <Text type="secondary" className="view-esim-details__qr-placeholder-text">
                {t('esim.viewDetailsModal.qrNotYetAvailable', {
                  defaultValue: 'Not claimed yet — QR will appear when claimed'
                })}
              </Text>
            </div>
          );
        }
        return (
          <div className="view-esim-details__qr-cell">
            <div
              className="view-esim-details__qr-wrap view-esim-details__qr-wrap--clickable"
              role="button"
              tabIndex={0}
              onClick={() =>
                onPreviewQr?.(
                  record.url,
                  `${t('esim.viewDetailsModal.qrCode', { defaultValue: 'QR Code' })} - ${record.promo_code}`
                )
              }
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onPreviewQr?.(record.url);
                }
              }}
              title={t('esim.viewDetailsModal.viewQrLarger', {
                defaultValue: 'Click to view larger'
              })}
              aria-label={t('esim.viewDetailsModal.viewQrLarger', {
                defaultValue: 'Click to view larger'
              })}
            >
              <QRCodeSVG value={record.url} size={64} level="M" />
            </div>
            <button
              type="button"
              className="view-esim-details__copy-btn"
              onClick={e => {
                e.stopPropagation();
                onCopyUrl(record.url);
              }}
              title={t('esim.viewDetailsModal.copyUrl', { defaultValue: 'Copy URL' })}
              aria-label={t('esim.viewDetailsModal.copyUrl', { defaultValue: 'Copy URL' })}
            >
              <CopyOutlined />
            </button>
          </div>
        );
      }
    },
    {
      title: t('esim.viewDetailsModal.promoCode', { defaultValue: 'Promo code' }),
      width: 120,
      dataIndex: 'promo_code',
      key: 'promo_code',
      ellipsis: true,
      render: (val: string) => <Text copyable={false}>{val ?? '—'}</Text>
    },
    {
      title: t('esim.viewDetailsModal.email', { defaultValue: 'Email' }),
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
      render: (val: string | null) => <Text>{val ?? '—'}</Text>
    },
    {
      title: t('esim.viewDetailsModal.status', { defaultValue: 'Status' }),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const key =
          status?.toLowerCase() === 'available'
            ? 'esim.viewDetailsModal.statusAvailable'
            : status?.toLowerCase() === 'claimed'
              ? 'esim.viewDetailsModal.statusClaimed'
              : status?.toLowerCase() === 'redeemed'
                ? 'esim.viewDetailsModal.statusRedeemed'
                : null;
        const label = key ? t(key, { defaultValue: status }) : status ?? '—';
        return <Tag color={getViewKinSimDetailsStatusBadgeColor(status)}>{label}</Tag>;
      }
    }
  ];
};
